import { setCustomViewWorking } from 'components/spreadsheet/components/custom-view/panel'
import React, { useState, useRef, useEffect, useLayoutEffect, useContext } from 'react'
import { useParams, useNavigate, generatePath } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { tv } from 'tailwind-variants'
import api, { IConversationListItem, IConversationMessage, IConversationMessageTool } from 'helpers/api'
import { useAuth } from 'hooks/auth'
import { withErrorCapture, CapturedError } from 'helpers/errorCapture'
import {
  EditorContent,
  IAgentStreamEvent,
  ICellValue,
  ITableRow,
  ITableViewColumn,
  ITableViewFilter,
  ITableViewRowLock,
  ITableViewWithColumns
} from 'types'
import Toggle from 'components/toggle'
import useProject from 'hooks/useProject'
import { AgentChatLaunchContext, useApplicationStore } from 'hooks/application'
import { DataContext as SpreadsheetDataContext } from 'components/spreadsheet/contexts/data/DataContext'
import { DataContext as DocumentDataContext } from 'components/document/contexts/data/DataContext'
import {
  updateColumn,
  createTableJoin,
  updateTableJoin,
  deleteTableJoin,
  saveCurrentView,
  updateTableViewCellsAction,
  deleteTableRowAction,
  deleteTableColumnAction,
  deleteView as deleteViewAction,
  updateView as updateViewAction
} from 'components/spreadsheet/contexts/data/actions'
import {
  convertNameMapToRowDataArray,
  buildNewColumn,
  convertToApiFilter,
  isColumnLockRule
} from 'components/spreadsheet/helpers/functions'
import { hydrateView, HydrateViewResult } from 'components/spreadsheet/helpers/hydrateView'
import { SELECT_COLUMN_TYPES, ViewTypes } from 'components/spreadsheet/constants/const'
import { sortDirections } from 'components/spreadsheet/types'
import { TABLE } from 'routes'
import { TourStep } from 'components/tour'
import TOURS, { canStartTour } from 'components/tour/tours'
import Spinner from 'components/spinner'
import { CHAT_FILE_ACCEPT } from 'components/chat/files'
import MessageBubble, { ChatMessage } from 'components/chat/MessageBubble'
import ConversationHistory from 'components/chat/ConversationHistory'
import {
  History,
  Copy,
  Expand,
  Plus,
  Cross,
  ArrowUp,
  ArrowDown,
  StopSquare,
  Sparkles,
  Attachment,
  Dashboard
} from 'components/icons'
import { hashString } from 'helpers/utils'
import { PanelSize, readPanelSize, clampSize, writePanelSize, nextPresetSize } from 'components/chat/panelSize'

const chat = tv({
  slots: {
    panel:
      'bottom-[84px] right-6 z-[9999] rounded-2xl border border-divider shadow-[0_16px_40px_rgba(0,0,0,0.10),0_4px_12px_rgba(0,0,0,0.06)] origin-bottom-right transition-[opacity,transform] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] fixed flex flex-col bg-surface overflow-hidden',
    header: 'flex items-center justify-between gap-3 px-4 py-3 border-b border-divider bg-surface select-none shrink-0',
    headerButton:
      'inline-flex items-center justify-center cursor-pointer p-0 w-7 h-7 rounded-md border-0 bg-transparent text-muted shadow-none transition-colors hover:bg-surface-sunken hover:text-body disabled:opacity-40 disabled:cursor-not-allowed [&_svg]:w-4 [&_svg]:h-4',
    suggestionChip:
      'px-3 py-2 border border-divider rounded-lg bg-surface cursor-pointer text-left text-[13px] text-body leading-[1.4] shadow-none transition-colors duration-150 hover:bg-surface-sunken',
    inputWrap: 'px-3 pb-3 pt-1 bg-surface shrink-0',
    inputField:
      'block w-full min-h-[40px] max-h-40 px-2 pt-1.5 pb-1 border-0 outline-none shadow-none leading-5 bg-transparent resize-none text-sm text-body placeholder:text-muted',
    fileChip: 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-surface border border-divider max-w-[180px]',
    fileChipRemove: 'text-[10px] cursor-pointer text-muted leading-none shrink-0 hover:text-red',
    attachBtn:
      'w-8 h-8 rounded-full p-0 inline-flex items-center justify-center bg-surface border border-divider text-muted cursor-pointer shrink-0 transition-colors hover:text-body hover:bg-surface-sunken',
    sendBtn:
      'w-8 h-8 rounded-full p-0 inline-flex items-center justify-center bg-blue border-0 shadow-none cursor-pointer text-on-solid shrink-0 transition-opacity hover:enabled:opacity-90 disabled:bg-surface-strong disabled:text-muted disabled:cursor-not-allowed',
    streamingThinking:
      'text-xs leading-relaxed text-muted bg-surface-sunken rounded-lg px-2.5 py-2 mb-1.5 whitespace-pre-wrap break-words max-h-[150px] overflow-y-auto',
    streamingThinkingLabel: 'text-[11px] text-blue font-medium mb-1 flex items-center gap-1',
    thinkingDot: 'w-1.5 h-1.5 rounded-full bg-blue animate-thinking-pulse'
  },
  variants: {
    disabled: {
      true: {
        attachBtn: 'opacity-40 cursor-not-allowed pointer-events-none'
      }
    },
    visible: {
      true: { panel: 'opacity-100 scale-100 translate-y-0' },
      false: { panel: 'opacity-0 scale-[0.92] translate-y-3' }
    },
    open: {
      false: { panel: 'hidden' }
    }
  }
})

const {
  header,
  headerButton,
  suggestionChip,
  inputWrap,
  inputField,
  fileChip,
  fileChipRemove,
  attachBtn,
  sendBtn,
  streamingThinking: streamingThinkingStyle,
  streamingThinkingLabel,
  thinkingDot,
  panel
} = chat()

interface ChatPanelProps {
  isOpen: boolean
  onClose: () => void
  onStartTour: (steps: TourStep[]) => void
  /** Docked into a page's right-hand panel (a Custom View's): fills its container, sized by the page. */
  docked?: boolean
  /** Sent as soon as the panel has it: a new Custom View's first prompt. A different prompt is sent again. */
  initialPrompt?: string
  /** Files attached to the first prompt, sent with it. */
  initialFiles?: File[]
  /** Once the first prompt is sent: the page then stops passing it, so reopening the panel doesn't send it again. */
  onInitialPromptSent?: () => void
}

const getSuggestedQuestions = (opts: {
  hasView: boolean
  hasDocument: boolean
  hasHub: boolean
  isSpreadsheetAdmin: boolean
  isDocumentAdmin: boolean
  isHubOwner: boolean
  isCustomView?: boolean
}): string[] => {
  const questions: string[] = []

  if (opts.isCustomView && opts.isSpreadsheetAdmin) {
    return [
      'Add a bar chart of the main numbers',
      'Add a filter so people can narrow the rows',
      'Make this page easier to read on a phone',
      'What can this view do with the data?'
    ]
  }

  if (opts.hasView) {
    questions.push('Summarise this table', 'What are the key insights from this data?')
    if (opts.isSpreadsheetAdmin) {
      questions.push('How can I improve this table schema?')
    }
  }

  if (opts.hasDocument) {
    questions.push('Summarise this document', 'What are the key takeaways?')
  }

  if (opts.hasHub && !opts.hasView && !opts.hasDocument) {
    questions.push('Give me an overview of this hub', 'What needs attention?')
  }

  questions.push('How do I create a new table?')

  return questions
}

/** Extract detailed error info from APIError (includes field-level validation errors). */
const describeApiError = (err: any): string => {
  const base = err?.message || 'Unknown error'
  const fieldErrors = err?.errors as
    | Array<{ attribute: string; messages: string[] | Record<string, string[]> }>
    | undefined
  if (!fieldErrors?.length) return base
  const details = fieldErrors
    .map((e) => {
      const msgs = Array.isArray(e.messages) ? e.messages.join(', ') : JSON.stringify(e.messages)
      return `${e.attribute}: ${msgs}`
    })
    .join('; ')
  return `${base} — ${details}`
}

/**
 * Cross-tab / cross-render dedup for support-chat error reports.
 * Returns true if a report with this fingerprint was already sent
 * within the last 5 seconds (caller should skip creating a duplicate
 * conversation), false otherwise. Records the fingerprint on miss.
 *
 * The fingerprint is hashed before being stored so the raw error
 * message and URL never sit in localStorage beyond the 5s window.
 */
const SUPPORT_REPORT_DEDUP_KEY = 'morta:lastSupportReport'
const SUPPORT_REPORT_DEDUP_WINDOW_MS = 5000

const isDuplicateSupportReport = (fingerprint: string): boolean => {
  const hashed = hashString(fingerprint)
  const writeFresh = () => {
    try {
      window.localStorage.setItem(SUPPORT_REPORT_DEDUP_KEY, JSON.stringify({ hash: hashed, timestamp: Date.now() }))
    } catch {
      // localStorage unavailable (private mode, quota exceeded); the
      // in-memory ref guard at the call site is the fallback.
    }
  }
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(SUPPORT_REPORT_DEDUP_KEY)
  } catch {
    raw = null
  }
  if (raw) {
    try {
      const prev = JSON.parse(raw) as { hash?: string; timestamp?: number }
      if (
        typeof prev.hash === 'string' &&
        typeof prev.timestamp === 'number' &&
        prev.hash === hashed &&
        Date.now() - prev.timestamp < SUPPORT_REPORT_DEDUP_WINDOW_MS
      ) {
        return true
      }
    } catch {
      // Corrupted JSON — fall through and overwrite with a fresh record
      // so dedup keeps working instead of being permanently disabled.
    }
  }
  writeFresh()
  return false
}

type ChatContextOverride = Pick<AgentChatLaunchContext, 'tableViewContextId' | 'documentContextId' | 'hubContextId'>

// Sent for the user once they accept every card from a reply that has more to do
// (`resumeAfterActions` on `done`). The server tells the model the planned step.
const RESUME_MESSAGE = "I've accepted the changes — carry on."
const RESUME_STOPPED_MESSAGE =
  "Not all of the changes were applied, so I've stopped here. Tell me how you'd like to continue."

// What the user did with the cards from the latest reply.
interface CardOutcomes {
  saving: Promise<void>[]
  declined: boolean
  // A change failed (even partly), or its outcome couldn't be saved.
  failed: boolean
  // Failures already sent back to the model (failAction).
  forwarded: boolean
}

const noCardOutcomes = (): CardOutcomes => ({ saving: [], declined: false, failed: false, forwarded: false })

// A dropped connection doesn't stop the reply: the server finishes and saves it, and the chat polls
// the conversation for it. Past the server's turn budget (MAX_TURN_SECONDS, 300s, plus a wrap-up
// round) and gunicorn's 500s timeout it isn't coming.
const RECOVERY_POLL_MS = 5000
const RECOVERY_TIMEOUT_MS = 9 * 60 * 1000
const RECOVERING_STATUS = 'Connection lost. Waiting for MortaAI to finish your reply…'
const RECOVERY_FAILED_MESSAGE =
  "The connection to MortaAI dropped and your reply didn't arrive. Send your message again to retry."
// Matches the reply the server saves for a stopped turn (STOPPED_REPLY_MESSAGE).
const STOPPED_MESSAGE = 'You stopped this reply before it finished.'

type QueuedAction = { action: string; data: Record<string, any> | null; toolUseId: string | null }

// The cards a reply proposed that are still unanswered, rebuilt as the `action` events that were lost
// with the connection. `resolvedAction` is that event's action name plus its data.
const unansweredCards = (tools: IConversationMessageTool[]): QueuedAction[] =>
  tools
    .filter((tool) => tool.resolvedAction && !tool.terminalState)
    .map((tool) => {
      const { action, ...data } = tool.resolvedAction!
      return { action, data: { ...data, skill_id: tool.skillId, tool_use_id: tool.publicId }, toolUseId: tool.publicId }
    })

function formatUpdateValue(raw: any): string {
  if (Array.isArray(raw)) return raw.join(', ')
  if (raw === true) return 'on'
  if (raw === false) return 'off'
  if (raw === null) return 'remove'
  return raw
}

function describeUpdateViewAction(actionData: any): React.ReactNode {
  const orderedNames = (actionData.columns_order || [])
    .map((c: { columnName: string }) => c.columnName)
    .filter(Boolean)
    .join(', ')
  const parts: React.ReactNode[] = []
  if (actionData.new_name) {
    parts.push(
      <>
        rename to <strong>{actionData.new_name}</strong>
      </>
    )
  }
  if (orderedNames) {
    parts.push(
      <>
        reorder columns to <strong>{orderedNames}</strong>
      </>
    )
  }
  if (actionData.chart_settings) {
    parts.push(<>update chart settings{actionData.chart_summary ? <> ({actionData.chart_summary})</> : null}</>)
  }
  if (parts.length === 0) {
    return null
  }
  return (
    <>
      Update view <strong>{actionData.view_name}</strong>:{' '}
      {parts.map((part, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 ? ', ' : ''}
          {part}
        </React.Fragment>
      ))}
    </>
  )
}

// The empty chat greets you by the time of day and your first name.
const greeting = (name?: string): string => {
  const hour = new Date().getHours()
  const part = hour < 12 ? 'Morning' : hour < 18 ? 'Afternoon' : 'Evening'
  const first = name?.trim().split(/\s+/)[0]
  return first ? `${part}, ${first}` : `Good ${part.toLowerCase()}`
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  isOpen,
  onClose,
  onStartTour,
  docked = false,
  initialPrompt,
  initialFiles,
  onInitialPromptSent
}) => {
  const navigate = useNavigate()
  const { project, projectOwner } = useProject()
  const spreadsheetCtx = useContext(SpreadsheetDataContext)
  const documentCtx = useContext(DocumentDataContext)
  const {
    displayErrorMessage,
    supportChatContext,
    openSupportChat,
    agentChatLaunchContext,
    clearAgentChatLaunchContext
  } = useApplicationStore()
  const { user, updateUser } = useAuth()

  const isHubOwner = projectOwner
  const isDocumentAdmin = isHubOwner || (documentCtx?.isAdmin ?? false)
  const isSpreadsheetAdmin = isHubOwner || (spreadsheetCtx?.spreadsheetData?.isAdmin ?? false)
  // On a Custom View its owners build and edit the page with MortaAI, so the empty chat asks about that.
  const editingCustomView =
    spreadsheetCtx?.spreadsheetData?.viewDetails?.type === ViewTypes.CUSTOM_VIEW && isSpreadsheetAdmin

  const { viewId, processId, projectId } = useParams<{
    viewId?: string
    processId?: string
    projectId?: string
  }>()
  const [contextOverride, setContextOverride] = useState<ChatContextOverride | null>(null)
  const activeViewId = contextOverride?.tableViewContextId ?? viewId
  const activeProcessId = contextOverride?.documentContextId ?? processId
  const activeProjectId = contextOverride?.hubContextId ?? projectId

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [conversationId, setConversationId] = useState<string | undefined>()
  const [notice, setNotice] = useState<string | null>(null)
  const [webSearch, setWebSearch] = useState(false)
  const [codeExecution, setCodeExecution] = useState(false)
  const [visible, setVisible] = useState(false)
  const [streamingThinking, setStreamingThinking] = useState('')
  const [streamingContent, setStreamingContent] = useState('')
  const [streamingStatus, setStreamingStatus] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<string | null>(null)
  const [actionData, setActionData] = useState<Record<string, any> | null>(null)
  const [actionQueue, setActionQueue] = useState<QueuedAction[]>([])
  const [actionErrors, setActionErrors] = useState<string[]>([])
  const [actionMessageId, setActionMessageId] = useState<string | null>(null)
  const [actionToolUseId, setActionToolUseId] = useState<string | null>(null)
  const [resumeAfterActions, setResumeAfterActions] = useState(false)
  const cardOutcomesRef = useRef<CardOutcomes>(noCardOutcomes())

  const [pendingJoinSave, setPendingJoinSave] = useState(false)
  const [attachedFiles, setAttachedFiles] = useState<File[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [conversationCopied, setConversationCopied] = useState(false)
  const [panelSize, setPanelSize] = useState<PanelSize>(() => clampSize(readPanelSize()))
  const abortRef = useRef<AbortController | null>(null)
  // The reply the current stream is writing (its `start` event), for Stop and for recovery.
  const replyIdRef = useRef<string | null>(null)
  const streamingContentRef = useRef('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setVisible(false)
      requestAnimationFrame(() => {
        setVisible(true)
        inputRef.current?.focus()
      })
    } else {
      setVisible(false)
    }
  }, [isOpen])

  useEffect(() => {
    const onResize = (): void => setPanelSize((prev) => clampSize(prev))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const togglePanelSize = (): void => {
    setPanelSize((prev) => {
      const next = nextPresetSize(prev)
      writePanelSize(next)
      return next
    })
  }

  const handleResizeStart = (e: React.PointerEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startY = e.clientY
    const startW = panelSize.width
    const startH = panelSize.height
    const prevUserSelect = document.body.style.userSelect
    document.body.style.userSelect = 'none'

    const onMove = (ev: PointerEvent | MouseEvent): void => {
      const dx = startX - ev.clientX // pointer moves left => wider
      const dy = startY - ev.clientY // pointer moves up   => taller
      setPanelSize(clampSize({ width: startW + dx, height: startH + dy }))
    }
    const onUp = (): void => {
      document.body.style.userSelect = prevUserSelect
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      setPanelSize((prev) => {
        writePanelSize(prev)
        return prev
      })
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    // pointercancel (touch interruption / OS gesture) shares the same teardown so
    // listeners and body.userSelect are never left dangling.
    window.addEventListener('pointercancel', onUp)
  }

  const conversationIdRef = useRef(conversationId)
  useEffect(() => {
    conversationIdRef.current = conversationId
  }, [conversationId])

  const deleteConversation = () => {
    if (!user) return
    const id = conversationIdRef.current
    if (!id) return
    conversationIdRef.current = undefined
    api.agentChatDelete(id)
  }

  // Walks away from the reply in progress: Stop, a new chat, another conversation. Aborting the stream
  // only drops the connection, which the server rides out so the reply can be recovered; this also ends
  // the reply there. Closing the page doesn't come through here, so that reply still lands in history.
  const abandonReply = () => {
    abortRef.current?.abort()
    abortRef.current = null
    const replyId = replyIdRef.current
    replyIdRef.current = null
    if (replyId && conversationIdRef.current) {
      api.agentChatCancel(conversationIdRef.current, replyId).catch(() => {
        // Best effort: the reply then finishes on the server and shows up in the conversation's history.
      })
    }
  }

  // Reset chat when context changes (e.g. navigating to a different view/document/hub)
  const contextKey = `${viewId || ''}-${processId || ''}-${projectId || ''}`
  const prevContextKey = useRef(contextKey)
  useEffect(() => {
    if (prevContextKey.current === contextKey) return
    prevContextKey.current = contextKey
    abandonReply()
    deleteConversation()
    setMessages([])
    setConversationId(undefined)
    setNotice(null)
    setStreamingThinking('')
    setStreamingContent('')
    setLoading(false)
    setPendingAction(null)
    setActionData(null)
    setActionToolUseId(null)
    setActionMessageId(null)
    setActionQueue([])
    setActionErrors([])
    setResumeAfterActions(false)
    setPendingJoinSave(false)
    setAttachedFiles([])
    setShowHistory(false)
    setContextOverride(null)
  }, [contextKey])

  // The transcript follows new content only while the reader is at the bottom; scrolling up leaves it
  // where they are, with a jump-to-latest button. A message you send is anchored near the top, with the
  // reply streaming in beneath it, rather than the whole thread sliding up under you.
  const scrollerRef = useRef<HTMLDivElement>(null)
  const followRef = useRef(true)
  const [atBottom, setAtBottom] = useState(true)
  const [anchorIndex, setAnchorIndex] = useState<number | null>(null)
  const [anchorSpace, setAnchorSpace] = useState(0)
  const messageCount = useRef(messages.length)

  const scrollToLatest = (behavior: ScrollBehavior = 'auto') => {
    const scroller = scrollerRef.current
    if (!scroller) return
    followRef.current = true
    scroller.scrollTo?.({ top: scroller.scrollHeight, behavior })
  }

  const handleTranscriptScroll = () => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const bottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 24
    followRef.current = bottom
    setAtBottom(bottom)
  }

  // A new message of yours becomes the anchor; a conversation loaded from history opens at its end.
  const pendingAnchorScroll = useRef(false)
  const spacerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const previous = messageCount.current
    messageCount.current = messages.length
    if (messages.length === previous + 1 && messages[messages.length - 1]?.role === 'user') {
      setAnchorIndex(messages.length - 1)
      pendingAnchorScroll.current = true
    } else if (messages.length === 0 || messages.length > previous + 1) {
      setAnchorIndex(null)
      setAnchorSpace(0)
      followRef.current = true
    }
  }, [messages])

  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const anchor =
      anchorIndex === null ? null : scroller.querySelector<HTMLElement>(`[data-message-index="${anchorIndex}"]`)
    if (anchor) {
      // Room below the anchor for it to reach the top; it shrinks as the reply fills it in, so the
      // transcript's height (and the reader's place) stays put while the reply streams.
      // Measured to where the content ends (the spacer's top), not scrollHeight, which never drops below
      // the panel's own height and would feed the space back into itself.
      const contentEnd = spacerRef.current?.offsetTop ?? anchor.offsetTop
      const space = Math.max(0, scroller.clientHeight - (contentEnd - anchor.offsetTop) - 16)
      if (space !== anchorSpace) {
        setAnchorSpace(space)
        return
      }
      if (pendingAnchorScroll.current) {
        pendingAnchorScroll.current = false
        scroller.scrollTop = anchor.offsetTop - 16
        return
      }
    }
    // Once the reply outgrows the panel (or with nothing anchored), follow it, if the reader is at the bottom.
    if (followRef.current && anchorSpace === 0) scroller.scrollTop = scroller.scrollHeight
  }, [messages, streamingThinking, streamingContent, streamingStatus, loading, anchorIndex, anchorSpace])

  useEffect(() => {
    if (!loading) {
      inputRef.current?.focus()
    }
  }, [loading])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  // Defer saveCurrentView until after the UPDATE_TABLE_JOINS reducer has
  // processed and spreadsheetData contains the new joined columns.
  useEffect(() => {
    if (pendingJoinSave && spreadsheetCtx && projectId) {
      saveCurrentView(
        projectId,
        spreadsheetCtx.spreadsheetData,
        spreadsheetCtx.setSpreadsheetData,
        () => {},
        () => {}
      )
      setPendingJoinSave(false)
    }
  }, [pendingJoinSave, spreadsheetCtx, projectId])

  // Dequeue the next action when the current one is dismissed/confirmed. Cards wait
  // for the reply to finish: its message id arrives on `done`, and an outcome
  // reported before then would be saved against the previous reply, or not at all.
  useEffect(() => {
    if (!loading && pendingAction === null && actionQueue.length > 0) {
      const [next, ...rest] = actionQueue
      setPendingAction(next.action)
      setActionData(next.data)
      setActionToolUseId(next.toolUseId)
      setActionQueue(rest)
    }
  }, [loading, pendingAction, actionQueue])

  const reportToolOutcome = (implemented: boolean, error?: Record<string, any>) => {
    const outcomes = cardOutcomesRef.current
    if (!implemented) outcomes.declined = true
    if (error) outcomes.failed = true
    if (actionMessageId && actionToolUseId) {
      // Kept so a resume waits until the server has every outcome; one it never
      // got would make the server refuse the resume anyway.
      outcomes.saving.push(
        api.agentChatToolOutcome(actionMessageId, actionToolUseId, implemented, error).catch(() => {
          outcomes.failed = true
        })
      )
    } else {
      outcomes.failed = true
    }
  }

  const clearAction = () => {
    setPendingAction(null)
    setActionData(null)
    setActionToolUseId(null)
  }

  const handleDismiss = () => {
    reportToolOutcome(false)
    clearAction()
  }

  // `partialFailure` names what didn't apply when a card only partly succeeded, so
  // the outcome is saved as failed and the reply isn't resumed as if it all worked.
  const completeAction = (message: string, partialFailure?: string) => {
    setMessages((prev) => [...prev, { role: 'agent', content: message }])
    reportToolOutcome(true, partialFailure ? { message: partialFailure } : undefined)
    clearAction()
  }

  const failAction = (errorMessage: string, capturedError?: CapturedError) => {
    cardOutcomesRef.current.forwarded = true
    reportToolOutcome(true, capturedError || { message: errorMessage })
    clearAction()
    setActionErrors((prev) => [...prev, errorMessage])
  }

  // MortaAI has already saved a Custom View page on the server as the view's newest version, so there's nothing to
  // confirm: the view it was for, if it's loaded here, shows it to its owner, and the bar over the page publishes it.
  useEffect(() => {
    if (pendingAction !== 'custom_view_version' || !actionData) return
    if (actionData.version) {
      spreadsheetCtx?.setSpreadsheetData({
        type: 'SET_CUSTOM_VIEW_LATEST',
        viewId: actionData.view_id,
        latest: { ...actionData.version, html: actionData.html }
      })
    }
    reportToolOutcome(true)
    clearAction()
  }, [pendingAction, actionData])

  const resetChat = () => {
    abandonReply()
    deleteConversation()
    setMessages([])
    setConversationId(undefined)
    setNotice(null)
    setContextOverride(null)
    setStreamingThinking('')
    setStreamingContent('')
    setStreamingStatus(null)
    setLoading(false)
    setPendingAction(null)
    setActionData(null)
    setActionToolUseId(null)
    setActionMessageId(null)
    setActionQueue([])
    setActionErrors([])
    setResumeAfterActions(false)
    setInput('')
    setAttachedFiles([])
    setShowHistory(false)
  }

  const copyConversation = async () => {
    if (messages.length === 0) return
    const text = messages.map((m) => `${m.role === 'user' ? 'You' : 'MortaAI'}: ${m.content}`).join('\n\n')
    try {
      await navigator.clipboard.writeText(text)
      setConversationCopied(true)
      setTimeout(() => setConversationCopied(false), 2000)
    } catch {
      // Clipboard API not available
    }
  }

  const loadConversation = async (item: IConversationListItem) => {
    setHistoryLoading(true)
    setShowHistory(false)
    let detail
    try {
      const response = await api.getConversation(item.publicId)
      detail = response.data
    } catch {
      setNotice('Failed to load conversation.')
      setHistoryLoading(false)
      return
    }

    abandonReply()
    setMessages(
      detail.messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
        thinking: msg.thinking || undefined,
        publicId: msg.publicId
      }))
    )
    setConversationId(detail.publicId)
    conversationIdRef.current = detail.publicId
    setNotice(null)
    setStreamingThinking('')
    setStreamingContent('')
    setStreamingStatus(null)
    setLoading(false)
    setPendingAction(null)
    setActionData(null)
    setActionToolUseId(null)
    setActionMessageId(null)
    setActionQueue([])
    setActionErrors([])
    setResumeAfterActions(false)
    setInput('')
    setAttachedFiles([])

    const contextMismatch =
      (detail.viewContextId && detail.viewContextId !== activeViewId) ||
      (detail.documentContextId && detail.documentContextId !== activeProcessId) ||
      (detail.hubContextId && detail.hubContextId !== activeProjectId)
    if (contextMismatch) {
      setNotice('This conversation was started in a different context. New messages will use your current context.')
    }
    setHistoryLoading(false)
  }

  // The Custom View the docked panel works on, named under the header.
  const focusedViewName =
    docked && spreadsheetCtx?.spreadsheetData?.viewDetails?.type === ViewTypes.CUSTOM_VIEW
      ? spreadsheetCtx.spreadsheetData.viewDetails.name
      : undefined

  // Docked beside a Custom View, the view's tab shows a spinner while MortaAI works on it.
  useEffect(() => {
    if (!docked) return
    setCustomViewWorking(loading)
  }, [docked, loading])
  useEffect(() => {
    if (!docked) return
    return () => setCustomViewWorking(false)
  }, [docked])

  // Docked beside a view, the chat is that view's one thread: it reopens the latest conversation held
  // about the view (newest first), so the conversation itself is the history.
  // A first prompt starts a fresh thread instead, and one sent while the lookup is out isn't replaced by it.
  const sentPromptRef = useRef<string | undefined>(undefined)
  const dockedViewId = docked ? (spreadsheetCtx?.spreadsheetData?.viewDetails?.publicId ?? viewId) : undefined
  useEffect(() => {
    if (!dockedViewId || !user || initialPrompt) return
    let cancelled = false
    api
      .getConversations(1, 20)
      .then((response) => {
        const thread = response.data.find((item) => item.viewContextId === dockedViewId)
        if (!cancelled && thread && !sentPromptRef.current) loadConversation(thread)
      })
      .catch(() => {
        // No thread to reopen; the panel starts a new one.
      })
    return () => {
      cancelled = true
    }
  }, [dockedViewId, user?.publicId, initialPrompt])

  const endTurn = () => {
    replyIdRef.current = null
    setStreamingThinking('')
    setStreamingContent('')
    setStreamingStatus(null)
    setLoading(false)
  }

  const endTurnWithMessage = (content: string) => {
    setMessages((prev) => [...prev, { role: 'agent', content }])
    endTurn()
  }

  const showRecoveredReply = (reply: IConversationMessage) => {
    setMessages((prev) => [
      ...prev,
      { role: 'agent', content: reply.content, thinking: reply.thinking || undefined, publicId: reply.publicId }
    ])
    setActionMessageId(reply.publicId)
    // Cards that arrived before the connection dropped are already queued.
    setActionQueue((prev) => {
      const queued = new Set(prev.map((card) => card.toolUseId))
      return [...prev, ...unansweredCards(reply.tools).filter((card) => !queued.has(card.toolUseId))]
    })
    setResumeAfterActions(Boolean(reply.resumeAfterActions))
    endTurn()
  }

  // The stream was cut off before `done`: poll the conversation until the reply it was writing is
  // saved. Aborting `controller` (Stop, a new chat, another conversation, closing) ends the wait.
  const recoverReply = (controller: AbortController) => {
    const conversation = conversationIdRef.current
    const replyId = replyIdRef.current
    // Only a signed-in user can read the conversation back; nobody will see this reply, so end it.
    if (!user || !conversation || !replyId) {
      abandonReply()
      endTurnWithMessage(RECOVERY_FAILED_MESSAGE)
      return
    }
    setStreamingStatus(RECOVERING_STATUS)
    const giveUpAt = Date.now() + RECOVERY_TIMEOUT_MS
    const poll = () => {
      const timer = setTimeout(async () => {
        let reply: IConversationMessage | undefined
        try {
          const response = await api.getConversation(conversation)
          reply = response.data.messages.find((message) => message.publicId === replyId)
        } catch {
          // Still offline, or the server is busy: keep waiting.
        }
        if (controller.signal.aborted) return
        if (reply) {
          showRecoveredReply(reply)
        } else if (Date.now() >= giveUpAt) {
          endTurnWithMessage(RECOVERY_FAILED_MESSAGE)
        } else {
          poll()
        }
      }, RECOVERY_POLL_MS)
      controller.signal.addEventListener('abort', () => clearTimeout(timer), { once: true })
    }
    poll()
  }

  const startStream = (text: string, context: ChatContextOverride | null, files?: File[], resume?: boolean) => {
    if (!text && (!files || files.length === 0)) return

    setMessages((prev) => [...prev, { role: 'user', content: text }])
    setLoading(true)
    setResumeAfterActions(false)
    cardOutcomesRef.current = noCardOutcomes()
    setStreamingThinking('')
    setStreamingContent('')
    streamingContentRef.current = ''
    setAttachedFiles([])

    let thinkingAccum = ''
    let contentAccum = ''
    replyIdRef.current = null

    const controller = api.agentChatStream(
      text,
      (event: IAgentStreamEvent) => {
        switch (event.type) {
          case 'start':
            setConversationId(event.conversationId)
            conversationIdRef.current = event.conversationId
            replyIdRef.current = event.messageId || null
            break
          case 'disconnected':
            recoverReply(controller)
            break
          case 'thinking':
            thinkingAccum += event.content || ''
            setStreamingThinking(thinkingAccum)
            break
          case 'content':
            contentAccum += event.content || ''
            streamingContentRef.current = contentAccum
            setStreamingContent(contentAccum)
            setStreamingStatus(null)
            break
          case 'status':
            setStreamingStatus(event.content || null)
            break
          case 'done':
            setConversationId(event.conversationId)
            setActionMessageId(event.messageId || null)
            setResumeAfterActions(Boolean(event.resumeAfterActions))
            if (contentAccum) {
              setMessages((prev) => [
                ...prev,
                {
                  role: 'agent',
                  content: contentAccum,
                  thinking: thinkingAccum || undefined,
                  publicId: event.messageId
                }
              ])
            } else if (event.messageId) {
              // Error responses are added by the error handler without a publicId.
              // Patch the last agent message so vote buttons appear.
              setMessages((prev) => {
                const last = prev.length - 1
                if (last >= 0 && prev[last].role === 'agent') {
                  const updated = [...prev]
                  updated[last] = { ...updated[last], publicId: event.messageId }
                  return updated
                }
                return prev
              })
            }
            endTurn()
            break
          case 'notice':
            setNotice(event.content || null)
            break
          case 'action':
            if (event.content) {
              setActionQueue((prev) => [
                ...prev,
                { action: event.content!, data: event.data || null, toolUseId: event.data?.tool_use_id || null }
              ])
            }
            break
          case 'error':
            replyIdRef.current = null
            setMessages((prev) => [
              ...prev,
              { role: 'agent', content: event.content || 'Sorry, something went wrong. Please try again.' }
            ])
            setStreamingThinking('')
            setStreamingContent('')
            setLoading(false)
            break
        }
      },
      conversationIdRef.current,
      context?.tableViewContextId,
      context?.documentContextId,
      context?.hubContextId,
      webSearch,
      codeExecution,
      files,
      Boolean(resume)
    )
    abortRef.current = controller
  }

  const sendText = (text: string, files?: File[], resume?: boolean) => {
    if (loading) return
    startStream(
      text,
      {
        tableViewContextId: activeViewId,
        documentContextId: activeProcessId,
        hubContextId: activeProjectId
      },
      files,
      resume
    )
  }

  // Ref so the support-context effect can call the latest sendText without
  // depending on it (which would cancel the timeout on every re-render).
  const sendTextRef = useRef(sendText)
  useEffect(() => {
    sendTextRef.current = sendText
  })

  useEffect(() => {
    if (!initialPrompt || sentPromptRef.current === initialPrompt) return
    sentPromptRef.current = initialPrompt
    sendTextRef.current(initialPrompt, initialFiles?.length ? initialFiles : undefined)
    onInitialPromptSent?.()
  }, [initialPrompt])

  // Send accumulated errors to the LLM after all actions have been processed
  useEffect(() => {
    if (pendingAction === null && actionQueue.length === 0 && actionErrors.length > 0) {
      const combined = actionErrors.join('\n')
      setActionErrors([])
      sendTextRef.current(combined)
    }
  }, [pendingAction, actionQueue, actionErrors])

  // Carry on by ourselves once every card from a reply that has more to do is
  // answered: resume if all were accepted, otherwise say why we stopped.
  useEffect(() => {
    if (!resumeAfterActions || loading || pendingAction !== null || actionQueue.length > 0) return
    setResumeAfterActions(false)
    const outcomes = cardOutcomesRef.current
    const conversation = conversationIdRef.current
    // Wait for every outcome to save: the server checks them before it lets the reply resume.
    Promise.all(outcomes.saving).then(() => {
      // A new message, a reset or another conversation since: not ours to resume.
      if (cardOutcomesRef.current !== outcomes || conversationIdRef.current !== conversation) return
      // Failures are already sent back to the model by the effect above.
      if (outcomes.forwarded) return
      if (outcomes.declined || outcomes.failed) {
        setMessages((prev) => [...prev, { role: 'agent', content: RESUME_STOPPED_MESSAGE }])
        return
      }
      sendTextRef.current(RESUME_MESSAGE, undefined, true)
    })
  }, [resumeAfterActions, loading, pendingAction, actionQueue])

  const supportContextHandled = useRef(false)

  useEffect(() => {
    if (!supportChatContext || supportContextHandled.current) return

    // Cross-tab / cross-render dedup: if the same error report was sent
    // (same message + URL) within the last 5 seconds — including from a
    // sibling tab in the same browser session — skip creating a duplicate
    // conversation. Belt-and-braces with the openSupportChat ref guard.
    const fingerprint = `${supportChatContext.errorMessage}::${supportChatContext.path || ''}::${window.location.href}`
    if (isDuplicateSupportReport(fingerprint)) {
      openSupportChat(null)
      return
    }

    supportContextHandled.current = true

    // Reset existing conversation before starting support chat
    resetChat()

    // Prompt for support access when diagnostics are present
    if (!user?.allowSupportAccess) {
      setPendingAction('enable_support_access')
    }

    // Build the diagnostic message
    const parts: string[] = ['I encountered an error and need help diagnosing it.']
    parts.push(`\n**Page URL:** ${window.location.href}`)
    parts.push(`**Error:** ${supportChatContext.errorMessage}`)
    if (supportChatContext.path) {
      parts.push(
        `**Endpoint:** ${supportChatContext.method ? `[${supportChatContext.method}] ` : ''}${supportChatContext.path}`
      )
    }
    parts.push(`\n${supportChatContext.diagnostics}`)

    const fullMessage = parts.join('\n')

    // Use setTimeout to let the reset render first, then send
    const timerId = setTimeout(() => {
      sendTextRef.current(fullMessage)
      // Clear the context so it doesn't re-trigger
      openSupportChat(null)
      supportContextHandled.current = false
    }, 100)

    return () => clearTimeout(timerId)
  }, [supportChatContext, openSupportChat, resetChat])

  useEffect(() => {
    if (!agentChatLaunchContext) return

    const initialInput = agentChatLaunchContext.initialInput?.trim() || ''
    const nextContextOverride: ChatContextOverride = {
      tableViewContextId: agentChatLaunchContext.tableViewContextId,
      documentContextId: agentChatLaunchContext.documentContextId,
      hubContextId: agentChatLaunchContext.hubContextId
    }
    const hasContextOverride = Boolean(
      nextContextOverride.tableViewContextId ||
        nextContextOverride.documentContextId ||
        nextContextOverride.hubContextId
    )
    const launchContext = hasContextOverride
      ? nextContextOverride
      : {
          tableViewContextId: viewId,
          documentContextId: processId,
          hubContextId: projectId
        }

    if (agentChatLaunchContext.reset !== false) {
      abandonReply()
      deleteConversation()
      setMessages([])
      setConversationId(undefined)
      setNotice(null)
      setStreamingThinking('')
      setStreamingContent('')
      setStreamingStatus(null)
      setLoading(false)
      setPendingAction(null)
      setActionData(null)
      setActionToolUseId(null)
      setActionMessageId(null)
      setActionQueue([])
      setActionErrors([])
      setResumeAfterActions(false)
      setAttachedFiles([])
      setShowHistory(false)
    }

    setContextOverride(hasContextOverride ? nextContextOverride : null)
    setInput(agentChatLaunchContext.autoSubmit ? '' : agentChatLaunchContext.initialInput || '')
    clearAgentChatLaunchContext()

    requestAnimationFrame(() => {
      inputRef.current?.focus()
    })

    if (agentChatLaunchContext.autoSubmit && initialInput) {
      startStream(initialInput, launchContext)
    }
  }, [agentChatLaunchContext, clearAgentChatLaunchContext, processId, projectId, viewId])

  const sendMessage = () => {
    const text = input.trim()
    if (!text) return
    setInput('')
    sendText(text, attachedFiles.length > 0 ? attachedFiles : undefined)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const handleEnableSupportAccess = async () => {
    try {
      const response = await api.enableSupportAccess()
      if (user) {
        await updateUser({
          ...user,
          allowSupportAccess: true,
          allowSupportAccessExpiresAt: response.data.expires_at
        })
      }
      setMessages((prev) => [
        ...prev,
        {
          role: 'agent',
          content:
            'Support access has been enabled for your account for the next 48 hours. The Morta team can now investigate your issue.'
        }
      ])
      reportToolOutcome(true)
      setPendingAction('email_support')
    } catch {
      reportToolOutcome(true, { message: 'Failed to enable support access' })
      setPendingAction(null)
      setMessages((prev) => [
        ...prev,
        {
          role: 'agent',
          content: 'Sorry, there was a problem enabling support access. Please try again from your account settings.'
        }
      ])
    }
  }

  const handleUpdateFormula = () => {
    if (!actionData || !spreadsheetCtx || !projectId) return
    updateColumn(
      projectId,
      'formula',
      spreadsheetCtx.spreadsheetData,
      spreadsheetCtx.setSpreadsheetData,
      actionData.column_id,
      actionData.formula,
      () => {
        completeAction(`Formula for "${actionData.column_name}" has been updated to: \`${actionData.formula}\``)
      },
      (error: any) => {
        failAction(
          `Formula error in column "${actionData.column_name}": ${describeApiError(error)}. Please fix the formula.`
        )
      }
    )
  }

  // ── Update columns handler (unified: name, kind, aggregate, description, header colours) ──
  const handleUpdateColumns = async () => {
    if (!actionData || !spreadsheetCtx || !projectId) return
    const updates: Array<{ column_id: string; column_name: string; field: string; value: any }> =
      actionData.updates || []
    if (updates.length === 0) return

    const succeeded: string[] = []
    const failed: string[] = []
    let firstError: unknown = null

    for (const { column_id, column_name, field, value } of updates) {
      let resolvedValue = value
      if (field === 'kindOptions' && Array.isArray(value)) {
        const cols = spreadsheetCtx.spreadsheetData.viewDetails.columns
        const existingCol = cols.find((c) => c.publicId === column_id)
        const existing = existingCol ? existingCol.kindOptions : undefined
        const tableOpts =
          existing && existing.tableOptions
            ? existing.tableOptions
            : {
                tableId: '',
                viewId: '',
                columnId: '',
                dependencies: [],
                liveValues: false
              }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { cachedOptions: _stripped, ...safeTableOptions } = tableOpts
        const autopopulate = existing && existing.autopopulate != null ? existing.autopopulate : false
        resolvedValue = {
          ...(existing ? existing : {}),
          manualOptions: value,
          tableOptions: safeTableOptions,
          autopopulate
        }
      }
      try {
        await new Promise<void>((resolve, reject) => {
          updateColumn(
            projectId,
            field as any,
            spreadsheetCtx.spreadsheetData,
            spreadsheetCtx.setSpreadsheetData,
            column_id,
            resolvedValue,
            resolve,
            reject
          )
        })
        succeeded.push(column_name)
      } catch (error) {
        failed.push(column_name)
        if (firstError === null) firstError = error
      }
    }

    const ok = succeeded.join(', ')
    const nope = failed.join(', ')
    if (succeeded.length > 0 && failed.length === 0) {
      completeAction(`Updated ${succeeded.length} column(s): ${ok}`)
    } else if (succeeded.length > 0) {
      completeAction(`Updated ${succeeded.length} column(s): ${ok}. Failed: ${nope}.`, `Failed to update: ${nope}`)
    } else {
      const base = `Failed to update columns: ${nope}`
      failAction(firstError ? `${base} (${describeApiError(firstError)})` : base)
    }
  }

  // ── Add columns handler ──
  const handleAddColumns = async () => {
    if (!actionData || !spreadsheetCtx || !project) return
    const columns: Array<{
      name: string
      kind?: string
      formula?: string
      manual_options?: string[]
      // Already in the column's rich text format: MortaAI's tool converts it.
      description?: EditorContent
    }> = actionData.columns || []
    if (columns.length === 0) return

    const viewId = spreadsheetCtx.spreadsheetData.viewDetails.publicId
    const added: Array<{ name: string; publicId: string }> = []
    const failed: string[] = []
    let firstError: unknown = null
    let nextSortOrder =
      Math.max(0, ...spreadsheetCtx.spreadsheetData.viewDetails.columns.map((col) => col.sortOrder)) + 1

    for (let i = 0; i < columns.length; i++) {
      const { name, kind, manual_options, description } = columns[i]
      const baseColumn = buildNewColumn(
        spreadsheetCtx.spreadsheetData,
        nextSortOrder,
        project.publicId,
        project.defaultHeaderBackgroundColor,
        project.defaultHeaderTextColor
      )
      nextSortOrder += 1
      const column = { ...baseColumn, name, kind: kind || 'text', ...(description && { description }) } as any
      if ((kind === 'select' || kind === 'multiselect') && manual_options && manual_options.length > 0) {
        column.kindOptions = { manualOptions: manual_options }
      }
      try {
        const response = await api.createTableColumn(viewId, column)
        const createdCol = response.data as ITableViewColumn
        spreadsheetCtx.setSpreadsheetData({ type: 'ADD_COLUMN', column: createdCol })
        added.push({ name, publicId: createdCol.publicId })
      } catch (error) {
        failed.push(name)
        if (firstError === null) firstError = error
      }
    }

    // Apply formulas for successfully added columns using captured IDs
    const formulaApplied: string[] = []
    const formulaFailed: string[] = []
    const addedNames = new Set(added.map((a) => a.name))
    const columnsWithFormulas = columns.filter((c) => c.formula && addedNames.has(c.name))

    if (columnsWithFormulas.length > 0 && projectId) {
      for (const { name, formula } of columnsWithFormulas) {
        const createdCol = added.find((a) => a.name === name)
        if (!createdCol) {
          formulaFailed.push(name)
          continue
        }
        try {
          await new Promise<void>((resolve, reject) => {
            updateColumn(
              projectId,
              'formula',
              spreadsheetCtx.spreadsheetData,
              spreadsheetCtx.setSpreadsheetData,
              createdCol.publicId,
              formula!,
              resolve,
              reject
            )
          })
          formulaApplied.push(name)
        } catch {
          formulaFailed.push(name)
        }
      }
    }

    const addedColumnNames = added.map((a) => a.name)
    let message = ''
    if (added.length > 0 && failed.length === 0) {
      message = `Added ${added.length} column(s): ${addedColumnNames.join(', ')}`
    } else if (added.length > 0) {
      message = `Added ${added.length} column(s): ${addedColumnNames.join(', ')}. Failed to add: ${failed.join(', ')}.`
    }
    if (formulaApplied.length > 0) {
      message += ` Formulas applied to: ${formulaApplied.join(', ')}.`
    }
    if (formulaFailed.length > 0) {
      message += ` Formula failed for: ${formulaFailed.join(', ')}.`
    }

    if (added.length > 0) {
      const partialFailures = [
        failed.length > 0 ? `Failed to add: ${failed.join(', ')}` : '',
        formulaFailed.length > 0 ? `Formula failed for: ${formulaFailed.join(', ')}` : ''
      ].filter(Boolean)
      completeAction(message, partialFailures.join('. ') || undefined)
    } else {
      const baseMessage = `Failed to add columns: ${failed.join(', ')}`
      failAction(firstError ? `${baseMessage} (${describeApiError(firstError)})` : baseMessage)
    }
  }

  // ── Sort handlers ──
  const handleManageSorts = () => {
    if (!actionData || !spreadsheetCtx) return
    const rules = actionData.sort_rules || []
    let added = 0
    let updated = 0
    let deleted = 0

    for (const r of rules) {
      const op = r.operation || 'add'
      if (op === 'add') {
        spreadsheetCtx.setSpreadsheetData({
          type: 'ADD_SORT',
          columnId: r.column_id,
          columnName: r.column_name
        })
        if (r.direction === 'desc') {
          spreadsheetCtx.setSpreadsheetData({
            type: 'CHANGE_SORT_DIRECTION',
            columnId: r.column_id,
            newDirection: sortDirections.desc
          })
        }
        added++
      } else if (op === 'update') {
        spreadsheetCtx.setSpreadsheetData({
          type: 'CHANGE_SORT_DIRECTION',
          columnId: r.column_id,
          newDirection: r.direction || 'asc'
        })
        updated++
      } else if (op === 'delete') {
        spreadsheetCtx.setSpreadsheetData({ type: 'DELETE_SORT', columnId: r.column_id })
        deleted++
      }
    }

    const parts = []
    if (added) parts.push(`${added} added`)
    if (updated) parts.push(`${updated} updated`)
    if (deleted) parts.push(`${deleted} removed`)
    completeAction(`Sort rules applied: ${parts.join(', ')}.`)
  }

  // ── Group handlers ──
  const handleManageGroups = () => {
    if (!actionData || !spreadsheetCtx) return
    const rules = actionData.group_rules || []
    let added = 0
    let updated = 0
    let deleted = 0

    for (const r of rules) {
      const op = r.operation || 'add'
      if (op === 'add') {
        spreadsheetCtx.setSpreadsheetData({
          type: 'ADD_GROUP',
          columnId: r.column_id,
          columnName: r.column_name
        })
        if (r.direction === 'desc') {
          spreadsheetCtx.setSpreadsheetData({
            type: 'CHANGE_GROUP_DIRECTION',
            columnId: r.column_id,
            newDirection: sortDirections.desc
          })
        }
        added++
      } else if (op === 'update') {
        spreadsheetCtx.setSpreadsheetData({
          type: 'CHANGE_GROUP_DIRECTION',
          columnId: r.column_id,
          newDirection: r.direction || 'asc'
        })
        updated++
      } else if (op === 'delete') {
        spreadsheetCtx.setSpreadsheetData({ type: 'DELETE_GROUP', columnId: r.column_id })
        deleted++
      }
    }

    const parts = []
    if (added) parts.push(`${added} added`)
    if (updated) parts.push(`${updated} updated`)
    if (deleted) parts.push(`${deleted} removed`)
    completeAction(`Group rules applied: ${parts.join(', ')}.`)
  }

  // ── Filter handlers ──
  // Full ITableViewFilter shape (both value AND multipleValues populated
  // with empty defaults) — matches the type the reducer expects for
  // UPDATE_FILTERS and the rest of the codebase's in-memory filter state.
  // convertToApiFilter() strips whichever of value/multipleValues isn't
  // used for the filter_type before the payload goes to the API, so the
  // backend FilterSchema's "exactly one of value/multipleValues" check is
  // still satisfied on the PUT.
  const toCamelFilter = (f: any): ITableViewFilter => ({
    columnId: f.column_id,
    columnName: f.column_name,
    filterType: f.filter_type,
    orGroup: f.or_group || 'main',
    value: f.value !== undefined && f.value !== null ? String(f.value) : '',
    multipleValues: (f.multiple_values || []).map((v: any) => (v == null ? '' : String(v)))
  })

  const handleManageFilters = () => {
    if (!actionData || !spreadsheetCtx) return

    // Apply-to-all-views branch: backend pre-computed the final filter list
    // for every view on the table. PUT each one via updateWholeTableView.
    if (actionData.apply_to_all_views && Array.isArray(actionData.view_updates)) {
      if (!projectId) {
        failAction('Unable to apply filters because no hub is selected.')
        return
      }
      const viewUpdates = actionData.view_updates as Array<{
        view_id: string
        view_name: string
        filter_settings: any[]
      }>
      const activeViewId = spreadsheetCtx.spreadsheetData.viewDetails.publicId
      // Apply sequentially rather than with Promise.all — each PUT enqueues a
      // join-recompute Celery task on the backend, and firing one per view
      // concurrently can overwhelm the API and Celery queue on tables with
      // many views. Returning the chain keeps the card disabled until it settles (#11356).
      return viewUpdates
        .reduce(
          (prev, vu) =>
            prev.then(() =>
              api.updateWholeTableView(vu.view_id, {
                filterSettings: convertToApiFilter(vu.filter_settings.map(toCamelFilter)),
                context: { projectId }
              } as any)
            ),
          Promise.resolve() as Promise<unknown>
        )
        .then(() => {
          // Also refresh the active view in memory so the UI reflects the
          // change without a reload, then dispatch SAVE_CURRENT_VIEW so
          // unsavedChanges flips back to false — otherwise the spreadsheet
          // stays stuck showing a Save prompt for filters that are already
          // persisted.
          const activeUpdate = viewUpdates.find((vu) => vu.view_id === activeViewId)
          if (activeUpdate) {
            spreadsheetCtx.setSpreadsheetData({
              type: 'UPDATE_FILTERS',
              filters: activeUpdate.filter_settings.map(toCamelFilter)
            })
            spreadsheetCtx.setSpreadsheetData({ type: 'SAVE_CURRENT_VIEW' })
          }
          completeAction(
            `Applied ${actionData.filter_rules?.length || 0} filter rule(s) to ${viewUpdates.length} view(s).`
          )
        })
        .catch((err) => {
          failAction(`Failed to apply filters to all views: ${err?.message || 'unknown error'}`)
        })
    }

    const rules = actionData.filter_rules || []
    let filters = spreadsheetCtx.spreadsheetData.userConfiguration.filterSettings || []
    let added = 0
    let updated = 0
    let deleted = 0

    for (const r of rules) {
      const op = r.operation || 'add'
      if (op === 'add') {
        filters = [
          ...filters,
          {
            columnId: r.column_id,
            columnName: r.column_name,
            filterType: r.filter_type,
            orGroup: r.or_group || 'main',
            value: r.value || '',
            multipleValues: r.multiple_values || []
          }
        ]
        added++
      } else if (op === 'update') {
        filters = filters.map((f: any) =>
          f.columnId === r.column_id
            ? {
                ...f,
                filterType: r.filter_type,
                value: r.value || '',
                multipleValues: r.multiple_values || f.multipleValues || [],
                orGroup: r.or_group || f.orGroup
              }
            : f
        )
        updated++
      } else if (op === 'delete') {
        filters = filters.filter((f: any) => f.columnId !== r.column_id)
        deleted++
      }
    }

    spreadsheetCtx.setSpreadsheetData({ type: 'UPDATE_FILTERS', filters })
    const parts = []
    if (added) parts.push(`${added} added`)
    if (updated) parts.push(`${updated} updated`)
    if (deleted) parts.push(`${deleted} removed`)
    completeAction(`Filter rules applied: ${parts.join(', ')}.`)
  }

  // ── Colour handlers ──
  const handleManageColours = () => {
    if (!actionData || !spreadsheetCtx) return
    const rules = actionData.colour_rules || []
    let colours = spreadsheetCtx.spreadsheetData.userConfiguration.colourSettings || []
    let added = 0
    let updated = 0
    let deleted = 0

    for (const r of rules) {
      const op = r.operation || 'add'
      if (op === 'add') {
        colours = [
          ...colours,
          {
            columnId: r.column_id,
            columnName: r.column_name,
            filterType: r.filter_type,
            value: r.value || '',
            multipleValues: [],
            backgroundColour: r.background_colour || '#ffffff',
            fontColour: r.font_colour || '#000000',
            fontBold: !!r.font_bold,
            fontItalic: !!r.font_italic,
            fontUnderline: !!r.font_underline,
            applyToEntireRow: !!r.apply_to_entire_row
          }
        ]
        added++
      } else if (op === 'update') {
        colours = colours.map((c: any) =>
          c.columnId === r.column_id
            ? {
                ...c,
                filterType: r.filter_type,
                value: r.value || '',
                backgroundColour: r.background_colour || c.backgroundColour,
                fontColour: r.font_colour || c.fontColour,
                fontBold: r.font_bold !== undefined ? !!r.font_bold : c.fontBold,
                fontItalic: r.font_italic !== undefined ? !!r.font_italic : c.fontItalic,
                fontUnderline: r.font_underline !== undefined ? !!r.font_underline : c.fontUnderline,
                applyToEntireRow: r.apply_to_entire_row !== undefined ? !!r.apply_to_entire_row : c.applyToEntireRow
              }
            : c
        )
        updated++
      } else if (op === 'delete') {
        colours = colours.filter((c: any) => c.columnId !== r.column_id)
        deleted++
      }
    }

    spreadsheetCtx.setSpreadsheetData({ type: 'UPDATE_COLOURS', colours })
    const parts = []
    if (added) parts.push(`${added} added`)
    if (updated) parts.push(`${updated} updated`)
    if (deleted) parts.push(`${deleted} removed`)
    completeAction(`Colour rules applied: ${parts.join(', ')}.`)
  }

  // ── Lock columns handler ──
  const handleLockColumns = () => {
    if (!actionData || !spreadsheetCtx) return
    const rules = actionData.column_locks || []
    if (rules.length === 0) return

    // Column locks are ALL_VALUES row-lock rules (apply_to_entire_row=false) —
    // the same representation the lock menu and the column right-click "Lock"
    // option use. Build the next rowLockSettings array and dispatch once.
    let locks = [...(spreadsheetCtx.spreadsheetData.userConfiguration.rowLockSettings || [])]
    let locked = 0
    let unlocked = 0
    for (const r of rules) {
      const op = (r.operation || '').toLowerCase()
      // Only the column-lock rule shape is added/removed here — conditional and
      // entire-row locks on the same column are left untouched.
      const hasColumnLock = locks.some((rule) => rule.columnId === r.column_id && isColumnLockRule(rule))
      if (op === 'lock') {
        // Only count a change when a rule is actually added (skip no-op re-locks).
        if (!hasColumnLock) {
          locks.push({
            columnId: r.column_id,
            columnName: r.column_name,
            filterType: 'all_values',
            value: '',
            multipleValues: [],
            applyToEntireRow: false
          } as ITableViewRowLock)
          locked++
        }
      } else if (op === 'unlock') {
        // Only count a change when a column-lock rule is actually removed.
        if (hasColumnLock) {
          locks = locks.filter((rule) => !(rule.columnId === r.column_id && isColumnLockRule(rule)))
          unlocked++
        }
      }
    }

    spreadsheetCtx.setSpreadsheetData({ type: 'UPDATE_LOCKS', locks })

    // The reducer marks the view as having unsaved changes; user persists via
    // the standard "Save View" button. This mirrors how manage_sorts /
    // manage_groups / manage_colours apply: they dispatch reducer actions
    // and leave the explicit save to the user.
    const parts: string[] = []
    if (locked) parts.push(`${locked} locked`)
    if (unlocked) parts.push(`${unlocked} unlocked`)
    completeAction(`Column locks applied: ${parts.join(', ')}. Click "Save View" to persist.`)
  }

  const handleBatchAddColours = () => {
    if (!actionData || !spreadsheetCtx) return
    const existing = spreadsheetCtx.spreadsheetData.userConfiguration.colourSettings || []
    const rules = (actionData.colour_rules || []).map((r: any) => ({
      columnId: r.columnId,
      columnName: r.columnName,
      filterType: r.filterType,
      value: r.value || '',
      multipleValues: r.multipleValues || [],
      backgroundColour: r.backgroundColour || '#ffffff',
      fontColour: r.fontColour || '#000000',
      fontBold: !!r.fontBold,
      fontItalic: !!r.fontItalic,
      fontUnderline: !!r.fontUnderline,
      applyToEntireRow: !!r.applyToEntireRow
    }))
    spreadsheetCtx.setSpreadsheetData({ type: 'UPDATE_COLOURS', colours: [...existing, ...rules] })
    completeAction(`${rules.length} colour rules added to "${actionData.column_name}".`)
  }

  // ── Join handlers ──
  const handleCreateJoin = () => {
    if (!actionData || !spreadsheetCtx || !projectId) return
    const joinPayload = {
      joinViewId: actionData.join_view_public_id,
      joinColumns: (actionData.join_columns || []).map((c: any) => ({
        sourceColumnId: c.sourceColumnPublicId,
        targetColumnId: c.targetColumnPublicId
      })),
      dataColumns: actionData.data_columns || [],
      isOneToMany: !!actionData.is_one_to_many
    }
    return new Promise<void>((settle) =>
      createTableJoin(
        spreadsheetCtx.spreadsheetData,
        spreadsheetCtx.setSpreadsheetData,
        joinPayload,
        projectId,
        () => {
          setPendingJoinSave(true)
          completeAction(`Lookup from "${actionData.source_table_name}" has been created.`)
          settle()
        },
        (error: any) => {
          failAction(`Error creating lookup from "${actionData.source_table_name}": ${describeApiError(error)}`)
          settle()
        }
      )
    )
  }

  const handleEditJoin = () => {
    if (!actionData || !spreadsheetCtx || !projectId) return
    const joinPayload = {
      joinViewId: actionData.join_view_public_id,
      joinColumns: (actionData.join_columns || []).map((c: any) => ({
        sourceColumnId: c.sourceColumnPublicId,
        targetColumnId: c.targetColumnPublicId
      })),
      dataColumns: actionData.data_columns || [],
      isOneToMany: !!actionData.is_one_to_many
    }
    return new Promise<void>((settle) =>
      updateTableJoin(
        spreadsheetCtx.spreadsheetData,
        spreadsheetCtx.setSpreadsheetData,
        actionData.join_id,
        joinPayload,
        projectId,
        () => {
          setPendingJoinSave(true)
          completeAction(`Lookup from "${actionData.source_table_name}" has been updated.`)
          settle()
        },
        (error: any) => {
          failAction(`Error updating lookup from "${actionData.source_table_name}": ${describeApiError(error)}`)
          settle()
        }
      )
    )
  }

  const handleDeleteJoin = () => {
    if (!actionData || !spreadsheetCtx || !projectId) return
    return new Promise<void>((settle) =>
      deleteTableJoin(
        spreadsheetCtx.spreadsheetData,
        spreadsheetCtx.setSpreadsheetData,
        actionData.join_id,
        projectId,
        () => {
          setPendingJoinSave(true)
          completeAction(`Lookup from "${actionData.source_table_name}" has been removed.`)
          settle()
        },
        (error: any) => {
          failAction(`Error removing lookup: ${describeApiError(error)}`)
          settle()
        }
      )
    )
  }

  // ── Create view handler ──
  const handleCreateView = async () => {
    if (!actionData || !spreadsheetCtx) return
    const tableId = actionData.table_id
    const viewData: any = {
      name: actionData.view_name,
      type: actionData.view_type,
      includeAllColumns: !!actionData.include_all_columns,
      context: projectId ? { projectId } : undefined
    }
    if (actionData.columns && actionData.columns.length > 0) {
      viewData.columns = actionData.columns
    }
    if (actionData.filter_settings && actionData.filter_settings.length > 0) {
      viewData.filterSettings = actionData.filter_settings
    }
    if (actionData.sort_settings && actionData.sort_settings.length > 0) {
      viewData.sortSettings = actionData.sort_settings
    }
    if (actionData.group_settings && actionData.group_settings.length > 0) {
      viewData.groupSettings = actionData.group_settings
    }
    if (actionData.colour_settings && actionData.colour_settings.length > 0) {
      viewData.colourSettings = actionData.colour_settings
    }
    if (actionData.chart_settings) {
      viewData.chartSettings = actionData.chart_settings
    }
    const result = await withErrorCapture(async () => {
      const response = await api.createNewView(tableId, viewData)
      const createdView = response.data as ITableViewWithColumns
      spreadsheetCtx.setSpreadsheetData({ type: 'ADD_VIEW', view: createdView, inProcess: false })
    })
    if (result.success) {
      completeAction(`${actionData.view_type_label} view "${actionData.view_name}" has been created.`)
    } else {
      failAction(
        `Error creating view "${actionData.view_name}": ${result.error?.message || 'Unknown error'}`,
        result.error
      )
    }
  }

  // ── Delete view handler ──
  const handleDeleteView = () => {
    if (!actionData || !spreadsheetCtx || !projectId) return
    return new Promise<void>((settle) =>
      deleteViewAction(
        spreadsheetCtx.setSpreadsheetData,
        projectId,
        actionData.view_id,
        () => {
          if (actionData.view_id === spreadsheetCtx.spreadsheetData.activeTableView) {
            const defaultViewId = spreadsheetCtx.spreadsheetData.tableDetails.defaultViewId
            spreadsheetCtx.setSpreadsheetData({ type: 'SET_VIEW', viewId: defaultViewId, inProcess: false })
            navigate(
              generatePath(TABLE, {
                projectId,
                tableId: spreadsheetCtx.spreadsheetData.tableDetails.publicId,
                viewId: defaultViewId
              }),
              { replace: true }
            )
          }
          completeAction(`View "${actionData.view_name}" has been deleted.`)
          settle()
        },
        (error: any) => {
          failAction(`Error deleting view "${actionData.view_name}": ${describeApiError(error)}`)
          settle()
        }
      )
    )
  }

  // ── Update view handler (rename, reorder columns, and/or update chart settings) ──
  const handleUpdateView = async () => {
    if (!actionData || !spreadsheetCtx || !projectId) return

    const { view_id, view_name, new_name, columns_order, active_view_id, chart_settings } = actionData
    const hasReorder = Array.isArray(columns_order) && columns_order.length > 0
    const hasChartSettings = !!chart_settings

    const describeChange = () => {
      const parts: string[] = []
      if (new_name) parts.push(`renamed to "${new_name}"`)
      if (hasReorder) parts.push('columns reordered')
      if (hasChartSettings) parts.push('chart settings updated')
      if (parts.length === 0) return `View "${view_name}" updated.`
      return `View "${view_name}": ${parts.join(', ')}.`
    }

    if (hasChartSettings && !hasReorder) {
      // Chart-settings-only update: persist via PUT /views/<id> directly so
      // the change applies to the target view regardless of which view the
      // user is currently looking at. The backend's table_view PUT endpoint
      // accepts chartSettings as a partial update.
      const payload: Partial<ITableViewWithColumns> & { context: { projectId: string } } = {
        chartSettings: chart_settings,
        context: { projectId }
      }
      if (new_name) {
        payload.name = new_name
      }
      let updated: ITableViewWithColumns
      try {
        const response = await api.updateWholeTableView(view_id, payload)
        updated = response.data
      } catch (error) {
        failAction(`Error updating chart settings on "${view_name}": ${describeApiError(error)}`)
        return
      }
      spreadsheetCtx.setSpreadsheetData({
        type: 'HYDRATE_VIEW',
        viewDetails: { ...updated, columnsHydrated: true }
      })
      // HYDRATE_VIEW updates tableView but not viewDetails/userConfiguration
      // for the currently active view, so chart-settings changes wouldn't be
      // visible until a view switch. Re-set the active view to remirror the
      // updated chart into userConfiguration.
      if (view_id === spreadsheetCtx.spreadsheetData.activeTableView) {
        spreadsheetCtx.setSpreadsheetData({
          type: 'SET_VIEW',
          viewId: view_id,
          inProcess: false,
          viewDetails: { ...updated, columnsHydrated: true }
        })
      }
      completeAction(describeChange())
      return
    }

    if (hasReorder) {
      // The active-view branch dispatches CHANGE_ALL_COLUMNS_ORDER, which
      // operates on whatever view is *currently rendered* in the spreadsheet
      // (no view ID parameter). If the user switched views between issuing
      // the chat request and confirming, we must NOT take that branch — it
      // would mutate the wrong view's reducer state. The backend-supplied
      // `active_view_id` was captured when the action was generated; we only
      // take the reducer branch when both (a) it still matches the live UI
      // state and (b) the chat request itself was about that view.
      const liveActiveViewId = spreadsheetCtx.spreadsheetData.activeTableView
      // Chart-settings updates can't go through the reducer (which only
      // handles column ordering), so a reorder bundled with chart_settings
      // always takes the PUT path.
      const isActiveView =
        !hasChartSettings && view_id === liveActiveViewId && (!active_view_id || view_id === active_view_id)

      // Non-active views are loaded with `ignoreColumns: true` and only have
      // their columns hydrated when the user switches to them (or when an
      // action like this one needs them). Hydrate first so the column list,
      // settings, and sort order we read are real.
      let targetView = spreadsheetCtx.spreadsheetData.tableView.find(
        (v: ITableViewWithColumns) => v.publicId === view_id
      )
      if (!targetView) {
        failAction(`Could not find view "${view_name}" to reorder.`)
        return
      }
      if (!targetView.columnsHydrated) {
        let hydrated: HydrateViewResult
        try {
          hydrated = await hydrateView(view_id, spreadsheetCtx.spreadsheetData.tableView)
        } catch (error) {
          failAction(`Error loading view "${view_name}": ${describeApiError(error)}`)
          return
        }
        spreadsheetCtx.setSpreadsheetData({ type: 'HYDRATE_VIEW', viewDetails: hydrated.view })
        if (hydrated.parent) {
          spreadsheetCtx.setSpreadsheetData({ type: 'HYDRATE_VIEW', viewDetails: hydrated.parent })
        }
        targetView = hydrated.view
      }

      const orderIds = (columns_order as Array<{ columnId: string }>).map((c) => c.columnId)

      if (isActiveView) {
        // Active-view reorder goes through the reducer (CHANGE_ALL_COLUMNS_ORDER)
        // — same UX as manual drag-and-drop. The user still has to click "Save
        // View" to persist, but that matches their muscle memory.
        // `targetView.columns` carries the "missing" columns appended by
        // mergeFetchedView (sortOrder: -1) which represent table columns not
        // yet in the view. By construction `getMissingColumns` reads those
        // off `userConfiguration.hiddenColumns`, so filtering the lookup pool
        // by hiddenColumns would drop them. Build `columnById` from the full
        // pool, and only exclude placeholders + user-hidden columns from
        // `remaining` (so unrequested ones stay out of the rendered set).
        const hiddenColumnIds = new Set(spreadsheetCtx.spreadsheetData.userConfiguration.hiddenColumns)
        const columnById = new Map(targetView.columns.map((c) => [c.publicId, c]))
        const visibleViewColumns = targetView.columns.filter(
          (c) => c.sortOrder !== -1 && !hiddenColumnIds.has(c.publicId)
        )
        const ordered = orderIds.map((id) => columnById.get(id)).filter((c): c is ITableViewColumn => Boolean(c))
        const remaining = visibleViewColumns.filter((c) => !orderIds.includes(c.publicId))
        const reordered: ITableViewColumn[] = [...ordered, ...remaining].map((c, idx) => ({ ...c, sortOrder: idx }))

        if (reordered.length === 0) {
          failAction(`Could not reorder columns for view "${view_name}": no columns matched.`)
          return
        }

        spreadsheetCtx.setSpreadsheetData({ type: 'CHANGE_ALL_COLUMNS_ORDER', columns: reordered })
        // CHANGE_ALL_COLUMNS_ORDER doesn't touch hiddenColumns, so any promoted
        // placeholder (or column the user had manually hidden) would still be
        // hidden after the reorder. Unhide each one we just added back.
        for (const column of ordered) {
          if (hiddenColumnIds.has(column.publicId)) {
            spreadsheetCtx.setSpreadsheetData({ type: 'SHOW_COLUMN', columnId: column.publicId })
          }
        }
        if (new_name) {
          updateViewAction(
            'name',
            spreadsheetCtx.spreadsheetData,
            spreadsheetCtx.setSpreadsheetData,
            view_id,
            new_name,
            projectId,
            () => {},
            () => {}
          )
        }
        completeAction(describeChange())
        return
      }

      // Non-active view: persist the reorder directly via PUT /views/<id>.
      // The backend re-creates table_view_columns from the supplied `columns`
      // array, so we must send the full ordered set with each column's
      // existing settings preserved (locked, required, validation, etc.).
      // The synthetic sortOrder=-1 placeholders (table columns not yet in
      // the view) are *included* in the lookup pool so manage_view can add
      // them by listing them in orderIds — promoting their sortOrder via
      // the .map below pulls them into the persisted column set. They are
      // NOT in `remaining`, so unrequested placeholders stay hidden.
      const lookupPool = targetView.columns
      const persistedColumns = lookupPool.filter((c) => c.sortOrder !== -1)
      const columnById = new Map(lookupPool.map((c) => [c.publicId, c]))
      const ordered = orderIds.map((id) => columnById.get(id)).filter((c): c is ITableViewColumn => Boolean(c))
      const remaining = persistedColumns.filter((c) => !orderIds.includes(c.publicId))
      const reordered: ITableViewColumn[] = [...ordered, ...remaining].map((c, idx) => ({ ...c, sortOrder: idx }))

      if (reordered.length === 0) {
        failAction(`Could not reorder columns for view "${view_name}": no columns matched.`)
        return
      }

      // Match saveCurrentView's column serialization: deep-clone so we don't
      // mutate state, default `required` (which is missing on some legacy
      // columns), and drop client-only `cachedOptions` from select/multiselect
      // kindOptions so they don't bloat the request or persist transient data.
      const apiColumns = reordered.map((column) => {
        const newColumn = JSON.parse(JSON.stringify(column))
        if (newColumn['required'] === undefined) newColumn['required'] = false
        if (SELECT_COLUMN_TYPES.includes(newColumn.kind) && newColumn.kindOptions?.tableOptions) {
          newColumn.kindOptions.tableOptions['cachedOptions'] = void 0
        }
        return newColumn
      })

      const payload: Partial<ITableViewWithColumns> & { context: { projectId: string } } = {
        columns: apiColumns,
        context: { projectId }
      }
      if (new_name) {
        payload.name = new_name
      }
      if (hasChartSettings) {
        payload.chartSettings = chart_settings
      }
      let updated: ITableViewWithColumns
      try {
        const response = await api.updateWholeTableView(view_id, payload)
        updated = response.data
      } catch (error) {
        failAction(`Error updating "${view_name}": ${describeApiError(error)}`)
        return
      }

      // Dispatch the authoritative server response synchronously before
      // completing the action so a follow-up reorder reads fresh column
      // data from `tableView[i]`. Doing this with a fire-and-forget
      // refetch instead would leave a window where columnsHydrated=true
      // but the columns array is stale — the next reorder would skip
      // re-hydration and build its payload from the pre-reorder state.
      spreadsheetCtx.setSpreadsheetData({
        type: 'HYDRATE_VIEW',
        viewDetails: { ...updated, columnsHydrated: true }
      })
      // When the bundle includes chart_settings and the user is currently
      // looking at this view, HYDRATE_VIEW alone won't refresh
      // userConfiguration so the chart wouldn't repaint until they
      // switched away and back. Mirror the chart-only branch.
      if (hasChartSettings && view_id === spreadsheetCtx.spreadsheetData.activeTableView) {
        spreadsheetCtx.setSpreadsheetData({
          type: 'SET_VIEW',
          viewId: view_id,
          inProcess: false,
          viewDetails: { ...updated, columnsHydrated: true }
        })
      }
      completeAction(describeChange())
      return
    }

    await new Promise<void>((settle) =>
      updateViewAction(
        'name',
        spreadsheetCtx.spreadsheetData,
        spreadsheetCtx.setSpreadsheetData,
        view_id,
        new_name,
        projectId,
        () => {
          completeAction(describeChange())
          settle()
        },
        (error: any) => {
          failAction(`Error renaming view "${view_name}": ${describeApiError(error)}`)
          settle()
        }
      )
    )
  }

  // ── Create table handler ──
  const handleCreateTable = async () => {
    if (!actionData || !projectId) return
    const { name, columns, rows, project_id } = actionData

    // Step 1: Create the table
    let table: any
    let viewId: string | null = null
    try {
      const response = await api.createNewTable({
        context: { projectId: project_id },
        projectId: project_id,
        name,
        columns: columns.map((c: any) => ({
          name: c.name,
          kind: c.kind,
          formula: c.formula,
          formulaEnabled: c.formula_enabled,
          description: c.description,
          kindOptions: c.kind_options
            ? { manualOptions: c.kind_options.manual_options, tableOptions: c.kind_options.table_options }
            : undefined
        }))
      })
      table = response.data
      viewId = table.defaultViewId
    } catch (err: any) {
      failAction(`Error creating table "${name}": ${describeApiError(err)}`)
      return
    }

    // Step 2: Insert rows (separate error handling so LLM knows what failed)
    // Strip values for formula and script columns — they are auto-computed or read-only
    const nonWritableColNames = new Set(
      columns.filter((c: any) => c.formula_enabled || c.script_enabled || c.script).map((c: any) => c.name)
    )
    const cleanedRows = (rows || []).map((r: Record<string, unknown>) => {
      const cleaned: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(r)) {
        if (!nonWritableColNames.has(k)) cleaned[k] = v
      }
      return cleaned
    })
    if (cleanedRows.length > 0 && viewId) {
      try {
        await api.createNewRows(
          project_id,
          viewId,
          cleanedRows.map((r: Record<string, unknown>) => ({ rowData: r }))
        )
      } catch (err: any) {
        const url = generatePath(TABLE, { projectId: project_id, tableId: table.publicId, viewId })
        window.open(url, '_blank')
        failAction(
          `Table "${name}" was created successfully, but inserting ${rows.length} row(s) failed: ${describeApiError(err)}. The table exists but is empty. Please fix the row data and try adding rows again.`
        )
        return
      }
    }

    if (viewId) {
      const url = generatePath(TABLE, { projectId: project_id, tableId: table.publicId, viewId })
      window.open(url, '_blank')
    }
    completeAction(
      `Table "${name}" has been created with ${columns.length} column(s)${rows?.length ? ` and ${rows.length} row(s)` : ''}.`
    )
  }

  // ── Add rows handler ──
  const handleAddRows = async () => {
    if (!actionData || !projectId || !spreadsheetCtx) return
    const { view_id, rows } = actionData
    const result = await withErrorCapture(async () => {
      const response = await api.createNewRows(
        projectId,
        view_id,
        rows.map((r: Record<string, unknown>) => ({ rowData: r }))
      )
      const newRows: ITableRow[] = response.data.map((responseRow: any) => {
        const rowDataArray = convertNameMapToRowDataArray(
          spreadsheetCtx.spreadsheetData.viewDetails.columns,
          responseRow.rowData as Record<string, ICellValue>,
          spreadsheetCtx.spreadsheetData.columnIdToIndex,
          spreadsheetCtx.spreadsheetData.indexToColumnId
        )

        if (spreadsheetCtx.spreadsheetData.columnStore) {
          const rowIndex = spreadsheetCtx.spreadsheetData.columnStore.addRow()
          for (let i = 0; i < rowDataArray.length; i++) {
            spreadsheetCtx.spreadsheetData.columnStore.set(rowIndex, i, rowDataArray[i])
          }
          return { ...responseRow, _rowIndex: rowIndex, rowData: undefined } as ITableRow
        }
        return { ...responseRow, rowData: rowDataArray, _rowIndex: -1 } as ITableRow
      })
      spreadsheetCtx.setSpreadsheetData({ type: 'APPEND_ROWS', rows: newRows })
    })
    if (result.success) {
      completeAction(`${rows.length} row(s) added successfully.`)
    } else {
      failAction(`Error adding rows: ${describeApiError(result.error)}`, result.error)
    }
  }

  // ── Edit rows handler ──
  const handleEditRows = () => {
    if (!actionData || !projectId || !spreadsheetCtx) return
    const { view_id, row_ids, updates, row_updates } = actionData
    const columns = spreadsheetCtx.spreadsheetData.viewDetails.columns

    // Build cells array from either per-row updates or uniform updates
    let cells: Array<{ rowId: string; columnId: string; value: ICellValue }>

    if (row_updates && Array.isArray(row_updates)) {
      // Per-row updates: each entry has row_id + updates
      cells = (row_updates as Array<{ row_id: string; updates: Record<string, ICellValue> }>).flatMap(
        ({ row_id, updates: rowUpd }) =>
          Object.entries(rowUpd)
            .map(([colName, value]) => {
              const col = columns.find((c) => c.name === colName)
              if (!col) return null
              return { rowId: row_id, columnId: col.publicId, value }
            })
            .filter(Boolean)
      ) as Array<{ rowId: string; columnId: string; value: ICellValue }>
    } else {
      // Uniform updates: same values applied to all row_ids
      cells = (row_ids as string[]).flatMap((rowId: string) =>
        Object.entries(updates as Record<string, ICellValue>)
          .map(([colName, value]) => {
            const col = columns.find((c) => c.name === colName)
            if (!col) return null
            return { rowId, columnId: col.publicId, value }
          })
          .filter(Boolean)
      ) as Array<{ rowId: string; columnId: string; value: ICellValue }>
    }

    return new Promise<void>((settle) =>
      updateTableViewCellsAction(
        view_id,
        { cells, context: { projectId } },
        spreadsheetCtx.spreadsheetData,
        spreadsheetCtx.setSpreadsheetData,
        () => {
          completeAction(`${actionData.row_count} row(s) updated successfully.`)
          settle()
        },
        (error: any) => {
          failAction(`Error updating rows: ${describeApiError(error)}`)
          settle()
        }
      )
    )
  }

  // ── Delete rows handler ──
  const handleDeleteRows = () => {
    if (!actionData || !projectId || !spreadsheetCtx) return
    const { row_ids } = actionData

    return new Promise<void>((settle) =>
      deleteTableRowAction(
        row_ids,
        projectId,
        spreadsheetCtx.spreadsheetData,
        spreadsheetCtx.setSpreadsheetData,
        () => {
          completeAction(`${row_ids.length} row(s) deleted successfully.`)
          settle()
        },
        () => {
          failAction(`Error deleting rows.`)
          settle()
        }
      )
    )
  }

  // ── Truncate table handler ──
  const handleTruncateTable = async () => {
    if (!actionData) return
    const { table_id } = actionData
    try {
      await api.truncateTable(table_id)
      if (spreadsheetCtx) {
        const allRowIds = spreadsheetCtx.spreadsheetData.rows.map((r) => r.publicId)
        spreadsheetCtx.setSpreadsheetData({ type: 'DELETE_ROWS', rowIds: allRowIds })
      }
      completeAction(`All rows deleted from "${actionData.table_name}".`)
    } catch (error) {
      failAction(`Error deleting all rows: ${describeApiError(error)}`)
    }
  }

  // ── Delete columns handler ──
  const handleDeleteColumns = async () => {
    if (!actionData || !projectId || !spreadsheetCtx) return
    const columns: Array<{ column_id: string; column_name: string }> = actionData.columns || []
    if (columns.length === 0) return

    const tableId = actionData.table_id
    const succeeded: string[] = []
    const failed: string[] = []

    for (const { column_id, column_name } of columns) {
      try {
        await new Promise<void>((resolve, reject) => {
          deleteTableColumnAction(tableId, column_id, projectId, spreadsheetCtx.setSpreadsheetData, resolve, reject)
        })
        succeeded.push(column_name)
      } catch {
        failed.push(column_name)
      }
    }

    const ok = succeeded.join(', ')
    const nope = failed.join(', ')
    if (succeeded.length > 0 && failed.length === 0) {
      completeAction(`Deleted ${succeeded.length} column(s): ${ok}`)
    } else if (succeeded.length > 0) {
      completeAction(`Deleted ${succeeded.length} column(s): ${ok}. Failed: ${nope}.`, `Failed to delete: ${nope}`)
    } else {
      failAction(`Failed to delete columns: ${nope}`)
    }
  }

  // ── Link select source handler ──
  // Configures a select / multiselect column so its options come from
  // another table's column. This is the "select source" feature, NOT
  // a join / lookup (which imports data columns via manage_join).
  const handleLinkSelectSource = () => {
    if (!actionData || !spreadsheetCtx || !projectId) return
    // Read the source-table name with a fallback to the legacy
    // payload key — during a rolling deploy the backend may still emit
    // ``lookup_table_name``, and we don't want the success message to
    // say "set to undefined".
    const sourceTableName = actionData.source_table_name ?? actionData.lookup_table_name
    return new Promise<void>((settle) =>
      updateColumn(
        projectId,
        'kindOptions',
        spreadsheetCtx.spreadsheetData,
        spreadsheetCtx.setSpreadsheetData,
        actionData.column_id,
        actionData.kind_options,
        () => {
          completeAction(`Column "${actionData.column_name}" select source set to "${sourceTableName}".`)
          settle()
        },
        (error: any) => {
          failAction(`Error linking select source: ${describeApiError(error)}`)
          settle()
        }
      )
    )
  }

  const stopGenerating = () => {
    abandonReply()
    endTurnWithMessage(streamingContentRef.current || STOPPED_MESSAGE)
  }

  const hasPendingActions = pendingAction !== null || actionQueue.length > 0
  const canSend = !loading && !hasPendingActions && input.trim().length > 0

  return (
    <div
      data-testid="chat-panel"
      className={
        docked ? 'relative flex flex-col w-full h-full bg-surface overflow-hidden' : panel({ visible, open: isOpen })
      }
      style={docked ? undefined : { width: panelSize.width, height: panelSize.height }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Pointer-only drag affordance. Keyboard/AT users resize via the header
          "Resize chat" toggle, so this is hidden from the a11y tree and tab order
          rather than being a focusable control that does nothing on Enter/Space. */}
      {!docked && (
        <button
          type="button"
          aria-hidden="true"
          tabIndex={-1}
          data-testid="chat-resize-handle"
          onPointerDown={handleResizeStart}
          className="absolute top-0 left-0 w-4 h-4 cursor-nwse-resize z-10 p-0 border-0 bg-transparent shadow-none outline-none"
        />
      )}
      {/* Header */}
      <div className={header()}>
        <div className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 font-semibold text-sm text-blue">MortaAI</span>
          {/* Docked beside a Custom View, the line goes on to name the view it's working on: "— Editing ▦ Name". */}
          {focusedViewName && (
            <span className="flex min-w-0 items-center gap-2 text-[13px]" data-testid="chat-focus">
              <span aria-hidden className="text-muted">
                —
              </span>
              <span className="shrink-0 text-muted">Editing</span>
              {/* The view's icon sits with its name, as on the view's tab. */}
              <span className="flex min-w-0 items-center gap-1 font-semibold text-body">
                <Dashboard className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{focusedViewName}</span>
              </span>
            </span>
          )}
          {loading && (
            <Spinner size="small" className="w-3.5 h-3.5 shrink-0 border-2" aria-label="MortaAI is working" />
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {!docked && (
            <button aria-label="Resize chat" title="Resize chat" onClick={togglePanelSize} className={headerButton()}>
              <Expand />
            </button>
          )}
          {user && !docked && (
            <button
              aria-label="Conversation history"
              title="Conversation history"
              onClick={() => setShowHistory((prev) => !prev)}
              className={headerButton({ class: showHistory ? 'bg-surface-sunken text-body' : undefined })}
            >
              <History />
            </button>
          )}
          {!docked && (
            <button
              aria-label={conversationCopied ? 'Copied!' : 'Copy conversation'}
              onClick={copyConversation}
              className={headerButton()}
              disabled={messages.length === 0}
              title={conversationCopied ? 'Copied!' : 'Copy conversation'}
            >
              {conversationCopied ? <span className="text-xs">✓</span> : <Copy />}
            </button>
          )}
          {!docked && (
            <button aria-label="New chat" title="New chat" onClick={resetChat} className={headerButton()}>
              <Plus />
            </button>
          )}
          <button aria-label="Close chat" title="Close" onClick={onClose} className={headerButton()}>
            <Cross />
          </button>
        </div>
      </div>

      {/* History or Messages */}
      {showHistory ? (
        <ConversationHistory onSelect={loadConversation} onClose={() => setShowHistory(false)} />
      ) : (
        <div
          ref={scrollerRef}
          onScroll={handleTranscriptScroll}
          className="relative flex-1 overflow-y-auto px-4 pt-4 bg-surface"
          data-testid="chat-transcript"
        >
          {messages.length === 0 && !loading && !historyLoading && (
            <div className="flex flex-col items-center text-center mt-10">
              <div className="flex justify-center items-center mb-4 w-10 h-10 rounded-xl border border-divider bg-surface-sunken text-body">
                <Sparkles />
              </div>
              <span className="text-base font-semibold text-body">{greeting(user?.name)}</span>
              <span className="text-sm text-muted mt-1 mb-6">
                {editingCustomView ? 'How would you like to update your view?' : 'What are we working on today?'}
              </span>
              <div className="flex flex-col gap-2 w-full py-0 px-1">
                {getSuggestedQuestions({
                  hasView: !!activeViewId,
                  hasDocument: !!activeProcessId,
                  hasHub: !!activeProjectId,
                  isSpreadsheetAdmin,
                  isDocumentAdmin,
                  isHubOwner,
                  isCustomView: editingCustomView
                }).map((q: string) => (
                  <button key={q} className={suggestionChip()} onClick={() => sendText(q)}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {notice && (
            <div className="text-xs text-muted mb-2 leading-normal py-2 px-3 rounded-lg bg-really-light-yellow border border-light-yellow">
              {notice}
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} data-message-index={i}>
              <MessageBubble
                message={msg}
                onVote={
                  msg.publicId
                    ? (vote, feedback) => {
                        setMessages((prev) => prev.map((m, idx) => (idx === i ? { ...m, vote } : m)))
                        api.agentChatVote(msg.publicId!, vote, feedback).catch((error) => {
                          displayErrorMessage(error)
                        })
                      }
                    : undefined
                }
              />
            </div>
          ))}

          {pendingAction === 'enable_support_access' && (
            <div className="animate-slide-in mb-3.5">
              <div className="bg-surface max-w-[90%] py-3.5 px-4 rounded-xl border border-divider">
                <div className="text-sm text-body mb-3 leading-[1.6]">
                  Enable support access so the Morta team can investigate your issue? This grants temporary read-only
                  access to your account for 48 hours.
                </div>
                <div className="flex gap-2">
                  <button
                    className="text-xs text-on-solid cursor-pointer border-0 py-1.5 px-3.5 rounded-lg bg-blue"
                    onClick={handleEnableSupportAccess}
                  >
                    Enable Support Access
                  </button>
                  <button
                    className="text-xs text-muted cursor-pointer py-1.5 px-3.5 rounded-lg bg-surface-muted border border-divider"
                    onClick={() => setPendingAction(null)}
                  >
                    No Thanks
                  </button>
                </div>
              </div>
            </div>
          )}

          {pendingAction === 'email_support' && (
            <div className="animate-slide-in mb-3.5">
              <div className="bg-surface max-w-[90%] py-3.5 px-4 rounded-xl border border-divider">
                <div className="text-sm text-body mb-3 leading-[1.6]">
                  Would you like to email this conversation to the Morta support team so they can follow up?
                </div>
                <div className="flex gap-2">
                  <a
                    className="text-xs text-on-solid cursor-pointer border-0 inline-flex items-center no-underline py-1.5 px-3.5 rounded-lg bg-blue"
                    href={`mailto:support@morta.io?subject=${encodeURIComponent('Support conversation')}&body=${encodeURIComponent(messages.map((m) => `${m.role === 'user' ? 'Me' : 'MortaAI'}: ${m.content}`).join('\n\n'))}`}
                    onClick={() => setPendingAction(null)}
                  >
                    Email Support
                  </a>
                  <button
                    className="text-xs text-muted cursor-pointer py-1.5 px-3.5 rounded-lg bg-surface-muted border border-divider"
                    onClick={() => setPendingAction(null)}
                  >
                    No Thanks
                  </button>
                </div>
              </div>
            </div>
          )}

          {pendingAction === 'update_formula' && actionData && (
            <div className="animate-slide-in mb-3.5">
              <div className="bg-surface max-w-[90%] py-3.5 px-4 rounded-xl border border-divider">
                <div className="text-sm text-body mb-3 leading-[1.6]">
                  Update the formula for <strong>{actionData.column_name}</strong> to:
                  <code className="block break-all mt-1.5 py-1.5 px-2.5 rounded-md text-xs bg-surface-muted">
                    {actionData.formula}
                  </code>
                </div>
                <div className="flex gap-2">
                  <button
                    className="text-xs text-on-solid cursor-pointer border-0 py-1.5 px-3.5 rounded-lg bg-blue"
                    onClick={handleUpdateFormula}
                  >
                    Update Formula
                  </button>
                  <button
                    className="text-xs text-muted cursor-pointer py-1.5 px-3.5 rounded-lg bg-surface-muted border border-divider"
                    onClick={handleDismiss}
                  >
                    No Thanks
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Update columns action ── */}
          {pendingAction === 'update_columns' && actionData && (
            <ViewActionCard
              description={
                <>
                  Update {actionData.updates?.length} column(s)
                  {actionData.updates?.length > 0 && (
                    <>
                      <br />
                      <span className="text-xs text-muted">
                        {actionData.updates
                          .map((u: any) => {
                            const label: Record<string, string> = {
                              name: 'rename',
                              kind: 'type',
                              kindOptions: 'options',
                              aggregate: 'aggregate',
                              description: 'description',
                              headerBackgroundColor: 'background',
                              headerTextColor: 'text colour',
                              stringValidation: 'regex',
                              validationMessage: 'error message',
                              hardValidation: 'strict validation',
                              validationNoBlanks: 'no blanks',
                              validationNoDuplicates: 'no duplicates',
                              thousandSeparator: 'thousand separator',
                              decimalPlaces: 'decimal places'
                            }
                            const display = formatUpdateValue(u.value)
                            return `${u.column_name}: ${label[u.field] || u.field} → ${display}`
                          })
                          .join(', ')}
                      </span>
                    </>
                  )}
                </>
              }
              confirmLabel={`Update ${actionData.updates?.length} Column(s)`}
              onConfirm={handleUpdateColumns}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Add columns action ── */}
          {pendingAction === 'add_columns' && actionData && (
            <ViewActionCard
              description={
                <>
                  Add {actionData.columns?.length} column(s)
                  {actionData.columns?.length > 0 && (
                    <>
                      <br />
                      <span className="text-xs text-muted">
                        {actionData.columns
                          .map((c: any) => {
                            const kind = c.kind || 'text'
                            if (
                              (kind === 'select' || kind === 'multiselect') &&
                              Array.isArray(c.manual_options) &&
                              c.manual_options.length > 0
                            ) {
                              return `${c.name} (${kind}: ${c.manual_options.join(', ')})`
                            }
                            return `${c.name} (${kind})`
                          })
                          .join(', ')}
                      </span>
                    </>
                  )}
                </>
              }
              confirmLabel={`Add ${actionData.columns?.length} Column(s)`}
              onConfirm={handleAddColumns}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Create table action ── */}
          {pendingAction === 'create_table' && actionData && (
            <ViewActionCard
              description={
                <>
                  Create table <strong>{actionData.name}</strong> with {actionData.columns?.length} column(s)
                  {actionData.columns?.length > 0 && (
                    <>
                      <br />
                      <span className="text-xs text-muted">
                        Columns: {actionData.columns.map((c: any) => c.name).join(', ')}
                      </span>
                    </>
                  )}
                  {actionData.rows?.length > 0 && (
                    <>
                      <br />
                      <span className="text-xs text-muted">{actionData.rows.length} row(s) will be added</span>
                    </>
                  )}
                </>
              }
              confirmLabel="Create Table"
              onConfirm={handleCreateTable}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Sort actions ── */}
          {pendingAction === 'manage_sorts' && actionData && (
            <ViewActionCard
              description={
                <>
                  Apply {(actionData.sort_rules || []).length} sort rule(s)
                  <br />
                  <span className="text-xs text-muted">
                    {(actionData.sort_rules || [])
                      .map((r: any) => {
                        const op = r.operation || 'add'
                        if (op === 'delete') return `remove "${r.column_name}"`
                        return `${op === 'add' ? '' : 'update '}${r.column_name} (${r.direction || 'asc'})`
                      })
                      .join(', ')}
                  </span>
                </>
              }
              confirmLabel="Apply Sorts"
              destructive={(actionData.sort_rules || []).every((r: any) => r.operation === 'delete')}
              onConfirm={handleManageSorts}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Group actions ── */}
          {pendingAction === 'manage_groups' && actionData && (
            <ViewActionCard
              description={
                <>
                  Apply {(actionData.group_rules || []).length} group rule(s)
                  <br />
                  <span className="text-xs text-muted">
                    {(actionData.group_rules || [])
                      .map((r: any) => {
                        const op = r.operation || 'add'
                        if (op === 'delete') return `remove "${r.column_name}"`
                        return `${op === 'add' ? '' : 'update '}${r.column_name} (${r.direction || 'asc'})`
                      })
                      .join(', ')}
                  </span>
                </>
              }
              confirmLabel="Apply Groups"
              destructive={(actionData.group_rules || []).every((r: any) => r.operation === 'delete')}
              onConfirm={handleManageGroups}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Filter actions ── */}
          {pendingAction === 'manage_filters' && actionData && (
            <ViewActionCard
              description={
                <>
                  Apply {(actionData.filter_rules || []).length} filter rule(s)
                  {actionData.apply_to_all_views && <> to all {(actionData.view_updates || []).length} view(s)</>}
                  <br />
                  <span className="text-xs text-muted">
                    {(actionData.filter_rules || [])
                      .slice(0, 10)
                      .map((r: any) => {
                        const op = r.operation || 'add'
                        if (op === 'delete') return `remove "${r.column_name}"`
                        return `${op} ${r.filter_type} on "${r.column_name}"`
                      })
                      .join(', ')}
                    {(actionData.filter_rules || []).length > 10 &&
                      ` +${(actionData.filter_rules || []).length - 10} more`}
                  </span>
                </>
              }
              confirmLabel={actionData.apply_to_all_views ? 'Apply to All Views' : 'Apply Filters'}
              destructive={(actionData.filter_rules || []).every((r: any) => r.operation === 'delete')}
              onConfirm={handleManageFilters}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Colour actions ── */}
          {pendingAction === 'manage_colours' && actionData && (
            <ViewActionCard
              description={
                <>
                  Apply {(actionData.colour_rules || []).length} colour rule(s)
                  <br />
                  <span className="flex flex-wrap gap-1 mt-1">
                    {(actionData.colour_rules || []).slice(0, 10).map((r: any, i: number) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded text-[11px]"
                        style={
                          r.operation === 'delete'
                            ? { background: 'var(--color-really-light-red)', color: 'var(--color-red)' }
                            : { background: r.background_colour || '#ffffff', color: r.font_colour || '#000000' }
                        }
                      >
                        {r.operation === 'delete' ? 'remove' : r.operation} &quot;{r.column_name}&quot;
                        {r.operation !== 'delete' && r.value && <>: {r.value}</>}
                      </span>
                    ))}
                    {(actionData.colour_rules || []).length > 10 && (
                      <span className="text-xs text-muted">+{(actionData.colour_rules || []).length - 10} more</span>
                    )}
                  </span>
                </>
              }
              confirmLabel="Apply Colours"
              destructive={(actionData.colour_rules || []).every((r: any) => r.operation === 'delete')}
              onConfirm={handleManageColours}
              onDismiss={handleDismiss}
            />
          )}

          {pendingAction === 'batch_add_colours' && actionData && (
            <ViewActionCard
              description={
                <>
                  Assign {(actionData.colour_rules || []).length} distinct colours to{' '}
                  <strong>{actionData.column_name}</strong>
                  <br />
                  <span className="flex flex-wrap gap-1 mt-1">
                    {(actionData.colour_rules || []).slice(0, 10).map((r: any, i: number) => (
                      <span
                        key={i}
                        className="inline-flex items-center px-1.5 py-[1px] rounded text-[11px]"
                        style={{ background: r.backgroundColour, color: r.fontColour }}
                      >
                        {r.value || (r.multipleValues || []).join(', ')}
                      </span>
                    ))}
                    {(actionData.colour_rules || []).length > 10 && (
                      <span className="text-xs text-muted">+{(actionData.colour_rules || []).length - 10} more</span>
                    )}
                  </span>
                </>
              }
              confirmLabel="Apply Colours"
              onConfirm={handleBatchAddColours}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Lock columns action ── */}
          {pendingAction === 'lock_columns' && actionData && (
            <ViewActionCard
              description={
                <>
                  Apply {(actionData.column_locks || []).length} column lock change(s)
                  <br />
                  <span className="text-xs text-muted">
                    {(actionData.column_locks || []).map((r: any) => `${r.operation} "${r.column_name}"`).join(', ')}
                  </span>
                </>
              }
              confirmLabel="Apply Locks"
              onConfirm={handleLockColumns}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Join actions ── */}
          {pendingAction === 'create_join' && actionData && (
            <ViewActionCard
              description={
                <>
                  Create lookup from <strong>{actionData.source_table_name}</strong>
                  <br />
                  <span className="text-xs text-muted">
                    Where{' '}
                    {(actionData.join_columns || [])
                      .map((c: any) => `${c.targetColumnName} = ${c.sourceColumnName}`)
                      .join(', ')}
                  </span>
                  <br />
                  <span className="text-xs text-muted">
                    Importing: {(actionData.data_column_names || []).join(', ')}
                  </span>
                  {actionData.is_one_to_many && (
                    <>
                      <br />
                      <span className="text-xs text-muted">One-to-many lookup</span>
                    </>
                  )}
                </>
              }
              confirmLabel="Create Lookup"
              onConfirm={handleCreateJoin}
              onDismiss={handleDismiss}
            />
          )}
          {pendingAction === 'edit_join' && actionData && (
            <ViewActionCard
              description={
                <>
                  Update lookup from <strong>{actionData.source_table_name}</strong>
                  <br />
                  <span className="text-xs text-muted">
                    Where{' '}
                    {(actionData.join_columns || [])
                      .map((c: any) => `${c.targetColumnName} = ${c.sourceColumnName}`)
                      .join(', ')}
                  </span>
                  <br />
                  <span className="text-xs text-muted">
                    Importing: {(actionData.data_column_names || []).join(', ')}
                  </span>
                  {actionData.is_one_to_many && (
                    <>
                      <br />
                      <span className="text-xs text-muted">One-to-many lookup</span>
                    </>
                  )}
                </>
              }
              confirmLabel="Update Lookup"
              onConfirm={handleEditJoin}
              onDismiss={handleDismiss}
            />
          )}
          {pendingAction === 'delete_join' && actionData && (
            <ViewActionCard
              description={
                <>
                  Remove lookup from <strong>{actionData.source_table_name}</strong>
                </>
              }
              confirmLabel="Remove Lookup"
              destructive
              onConfirm={handleDeleteJoin}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Create view action ── */}
          {pendingAction === 'create_view' && actionData && (
            <ViewActionCard
              description={
                <>
                  Create {actionData.view_type_label} view: <strong>{actionData.view_name}</strong>
                  {actionData.column_names && actionData.column_names.length > 0 && (
                    <>
                      <br />
                      <span className="text-xs text-muted">Columns: {actionData.column_names.join(', ')}</span>
                    </>
                  )}
                  {actionData.include_all_columns && (
                    <>
                      <br />
                      <span className="text-xs text-muted">All columns</span>
                    </>
                  )}
                  {actionData.filter_descriptions && actionData.filter_descriptions.length > 0 && (
                    <>
                      <br />
                      <span className="text-xs text-muted">Filters: {actionData.filter_descriptions.join('; ')}</span>
                    </>
                  )}
                  {actionData.sort_descriptions && actionData.sort_descriptions.length > 0 && (
                    <>
                      <br />
                      <span className="text-xs text-muted">Sorted by: {actionData.sort_descriptions.join(', ')}</span>
                    </>
                  )}
                  {actionData.group_descriptions && actionData.group_descriptions.length > 0 && (
                    <>
                      <br />
                      <span className="text-xs text-muted">Grouped by: {actionData.group_descriptions.join(', ')}</span>
                    </>
                  )}
                  {actionData.chart_description && (
                    <>
                      <br />
                      <span className="text-xs text-muted">Chart: {actionData.chart_description}</span>
                    </>
                  )}
                </>
              }
              confirmLabel="Create View"
              onConfirm={handleCreateView}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Delete view action ── */}
          {pendingAction === 'delete_view' && actionData && (
            <ViewActionCard
              description={
                <>
                  Delete view: <strong>{actionData.view_name}</strong>
                </>
              }
              confirmLabel="Delete View"
              destructive
              onConfirm={handleDeleteView}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Update view action (rename, reorder columns, and/or chart settings) ── */}
          {pendingAction === 'update_view' && actionData && (
            <ViewActionCard
              description={describeUpdateViewAction(actionData)}
              confirmLabel={(() => {
                const hasReorder = !!actionData.columns_order?.length
                const hasChart = !!actionData.chart_settings
                const hasRename = !!actionData.new_name
                const flags = [hasRename, hasReorder, hasChart].filter(Boolean).length
                if (flags > 1) return 'Update View'
                if (hasRename) return 'Rename View'
                if (hasReorder) return 'Reorder Columns'
                if (hasChart) return 'Update Chart Settings'
                return 'Update View'
              })()}
              onConfirm={handleUpdateView}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Add rows action ── */}
          {pendingAction === 'add_rows' && actionData && (
            <ViewActionCard
              description={
                <>
                  Add <strong>{actionData.row_count}</strong> row(s) to <strong>{actionData.table_name}</strong>
                </>
              }
              confirmLabel="Add Rows"
              onConfirm={handleAddRows}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Edit rows action ── */}
          {pendingAction === 'edit_rows' && actionData && (
            <ViewActionCard
              description={
                <>
                  Update <strong>{actionData.row_count}</strong> row(s) in <strong>{actionData.table_name}</strong>
                  <br />
                  <span className="text-xs text-muted">Changing: {(actionData.column_changes || []).join(', ')}</span>
                </>
              }
              confirmLabel="Update Rows"
              onConfirm={handleEditRows}
              onDismiss={handleDismiss}
            />
          )}

          {/* ── Delete rows action ── */}
          {pendingAction === 'delete_rows' && actionData && (
            <ViewActionCard
              description={
                <>
                  Delete <strong>{actionData.row_count}</strong> row(s) from <strong>{actionData.table_name}</strong>
                </>
              }
              confirmLabel="Delete Rows"
              onConfirm={handleDeleteRows}
              onDismiss={handleDismiss}
              destructive
            />
          )}

          {/* ── Truncate table action ── */}
          {pendingAction === 'truncate_table' && actionData && (
            <ViewActionCard
              description={
                <>
                  Delete <strong>all rows</strong> from <strong>{actionData.table_name}</strong>
                </>
              }
              confirmLabel="Delete All Rows"
              onConfirm={handleTruncateTable}
              onDismiss={handleDismiss}
              destructive
            />
          )}

          {/* ── Delete columns action ── */}
          {pendingAction === 'delete_columns' && actionData && (
            <ViewActionCard
              description={
                <>
                  Delete <strong>{actionData.columns?.length}</strong> column(s)
                  {actionData.columns?.length > 0 && (
                    <>: {actionData.columns.map((c: any) => c.column_name).join(', ')}</>
                  )}
                </>
              }
              confirmLabel={`Delete ${actionData.columns?.length} Column(s)`}
              onConfirm={handleDeleteColumns}
              onDismiss={handleDismiss}
              destructive
            />
          )}

          {/* ── Link select source action ──
              Accept the legacy ``link_lookup`` action id and the legacy
              ``lookup_table_name`` payload key as well so a rolling
              deploy (old backend → new frontend) doesn't deadlock the
              chat: pendingAction wouldn't match, no card would render,
              but hasPendingActions would still be true. */}
          {(pendingAction === 'link_select_source' || pendingAction === 'link_lookup') && actionData && (
            <ViewActionCard
              description={
                <>
                  Set the select source for column <strong>{actionData.column_name}</strong> to{' '}
                  <strong>{actionData.source_table_name ?? actionData.lookup_table_name}</strong>
                </>
              }
              confirmLabel="Link Select Source"
              onConfirm={handleLinkSelectSource}
              onDismiss={handleDismiss}
            />
          )}

          {pendingAction === 'product_tour' && actionData && (
            <div className="animate-slide-in mb-3.5">
              <div className="bg-surface max-w-[90%] py-3.5 px-4 rounded-xl border border-divider">
                <div className="text-sm text-body mb-3 leading-[1.6]">I can walk you through this step by step.</div>
                {actionData.fallback_steps && actionData.fallback_steps.length > 0 && (
                  <ol className="text-xs text-muted leading-[1.8] mb-3 pl-4 list-decimal">
                    {actionData.fallback_steps.map((step: string, i: number) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                )}
                <div className="flex gap-2">
                  {/* An Owner-only tour (sidebar folders) isn't offered to anyone else: the steps stay. */}
                  {(!TOURS[actionData.tour_id] || canStartTour(TOURS[actionData.tour_id], isHubOwner)) && (
                    <button
                      className="text-xs text-on-solid cursor-pointer border-0 py-1.5 px-3.5 rounded-lg bg-blue"
                      onClick={() => {
                        const tour = TOURS[actionData.tour_id]
                        if (tour) {
                          onStartTour(tour.steps)
                          onClose()
                        }
                        reportToolOutcome(true)
                        clearAction()
                      }}
                    >
                      Show Me How
                    </button>
                  )}
                  <button
                    className="text-xs text-muted cursor-pointer py-1.5 px-3.5 rounded-lg bg-surface-muted border border-divider"
                    onClick={handleDismiss}
                  >
                    No Thanks
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading && (streamingThinking || streamingContent) && (
            <div className="animate-slide-in mb-3.5">
              {streamingThinking && (
                <>
                  <div className={streamingThinkingLabel()}>
                    <span className={thinkingDot()} />
                    Thinking...
                  </div>
                  <div className={streamingThinkingStyle()}>{streamingThinking}</div>
                </>
              )}
              {streamingContent && (
                <div className="text-sm text-body chat-markdown break-words leading-[1.6]">
                  <ReactMarkdown
                    disallowedElements={['img']}
                    components={{
                      a: (props: any) => (
                        <a {...props} rel="noreferrer noopener" target="_blank">
                          {props.children}
                        </a>
                      )
                    }}
                  >
                    {streamingContent}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          )}

          {streamingStatus && (
            <div className="animate-slide-in flex items-center gap-2 text-muted py-1.5 px-0 text-xs">
              <LoadingDots />
              <span>{streamingStatus}</span>
            </div>
          )}

          {loading && !streamingThinking && !streamingContent && !streamingStatus && (
            <div className="animate-slide-in flex items-center gap-2 py-2 px-0">
              <LoadingDots />
            </div>
          )}

          {/* Room below an anchored message, so it can sit at the top while the reply streams in under it. */}
          <div ref={spacerRef} style={{ height: anchorSpace }} aria-hidden="true" />
          <div className="h-2" />
          {/* Pinned to the bottom of the visible transcript while it's scrolled up from the latest. */}
          {!atBottom && (
            <div className="sticky bottom-0 h-0 -mx-4">
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b from-transparent to-[var(--color-surface)]" />
              <button
                type="button"
                aria-label="Jump to latest"
                title="Jump to latest"
                onClick={() => scrollToLatest('smooth')}
                className={headerButton({
                  class:
                    'absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full border border-divider bg-surface shadow-sm'
                })}
              >
                <ArrowDown />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Input */}
      <div className={inputWrap({ class: docked ? 'rounded-none' : undefined })}>
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {attachedFiles.map((file, i) => (
              <span key={`${file.name}-${file.size}-${file.lastModified}`} className={fileChip()}>
                <span className="text-xs text-ellipsis whitespace-nowrap overflow-hidden max-w-[140px]">
                  {file.name}
                </span>
                <button
                  type="button"
                  className={fileChipRemove()}
                  onClick={() => setAttachedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  aria-label={`Remove ${file.name}`}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="rounded-2xl bg-surface-sunken p-2 focus-within:ring-2 focus-within:ring-blue/20">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything…"
            disabled={loading}
            className={inputField()}
          />
          <div className="flex items-center justify-between mt-1">
            <input
              ref={fileInputRef}
              id="chat-file-input"
              type="file"
              multiple
              accept={CHAT_FILE_ACCEPT}
              className="absolute overflow-hidden pointer-events-none w-0 h-0 opacity-0"
              onChange={(e) => {
                const newFiles = Array.from(e.target.files || [])
                if (newFiles.length > 0) {
                  setAttachedFiles((prev) => [...prev, ...newFiles])
                }
                e.target.value = ''
              }}
            />
            <label
              htmlFor="chat-file-input"
              aria-label="Attach file"
              className={attachBtn({ disabled: loading })}
              title="Attach file"
            >
              <Attachment />
            </label>
            {loading ? (
              <button onClick={stopGenerating} className={sendBtn()} aria-label="Stop generating">
                <StopSquare className="w-3 h-3 fill-current" />
              </button>
            ) : (
              <button onClick={sendMessage} disabled={!canSend} className={sendBtn()} aria-label="Send">
                <ArrowUp />
              </button>
            )}
          </div>
        </div>

        {false && (
          <div className="flex items-center gap-4 mt-2 mb-2">
            <div className="flex items-center">
              <Toggle aria-label="Web Search" toggled={webSearch} onToggle={() => setWebSearch((v) => !v)} />
              <span className="text-sm ml-[5px]">Web Search</span>
            </div>

            <div className="flex items-center">
              <Toggle
                aria-label="Code Execution"
                toggled={codeExecution}
                onToggle={() => setCodeExecution((v) => !v)}
              />
              <span className="text-sm ml-[5px]">Code Execution</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const ViewActionCard: React.FC<{
  description: React.ReactNode
  confirmLabel: string
  onConfirm: () => void | Promise<void>
  onDismiss: () => void
  destructive?: boolean
}> = ({ description, confirmLabel, onConfirm, onDismiss, destructive }) => {
  // Disable the card until the promise onConfirm returns settles, so extra clicks while the action is running don't
  // run it again (#11356). A handler that sends a request must return a promise that settles once the request has
  // reported back; ones that finish through success/error callbacks wrap them in a promise to do that.
  const [busy, setBusy] = useState(false)
  const confirm = async () => {
    if (busy) return
    setBusy(true)
    // try/catch rather than try/finally: the React Compiler can't compile a try without a catch.
    try {
      await onConfirm()
    } catch (error) {
      setBusy(false)
      throw error
    }
    setBusy(false)
  }
  return (
    <div className="animate-slide-in mb-3.5">
      <div className="bg-surface max-w-[90%] py-3.5 px-4 rounded-xl border border-divider">
        <div className="text-sm text-body mb-3 leading-[1.6]">{description}</div>
        <div className="flex gap-2">
          <button
            className={`text-xs text-on-solid cursor-pointer border-0 py-1.5 px-3.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${destructive ? 'bg-red' : 'bg-blue'}`}
            onClick={confirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
          <button
            className="text-xs text-muted cursor-pointer py-1.5 px-3.5 rounded-lg bg-surface-muted border border-divider disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onDismiss}
            disabled={busy}
          >
            No Thanks
          </button>
        </div>
      </div>
    </div>
  )
}

const LoadingDots: React.FC = () => (
  <div className="inline-flex items-center gap-1.5 py-2">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="rounded-full w-1.5 h-1.5 bg-muted"
        style={{
          animation: `dot-pulse 1.4s ease-in-out ${i * 0.2}s infinite`
        }}
      />
    ))}
  </div>
)

export default ChatPanel
